
/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */

import './bootstrap';

import { createApp, defineAsyncComponent } from 'vue';
import { installTours } from './tours/index.js';

// ── Component imports — bundle-split philosophy ───────────────────────────────
// What's kept STATIC below: small utility components + layout shells +
// anything visible on every page. These have to be in the main bundle
// because they're used by whatever lands first.
//
// What's loaded ASYNC (further down, via defineAsyncComponent in the
// app.component() calls): every "page" component for client / admin /
// form panels. Each lands in its own Vite chunk and is downloaded only
// when the user actually navigates to that page. The biggest single
// win is client/Aml.vue — it pulls in the entire AML SPA subtree
// (CDD, escalations, reporting, associated persons, etc.) which was
// previously bloating every cold load with ~1.5MB of code most users
// never touch.

// ── Component imports: Layout + always-on (kept static) ───────────────────────
import ClientDefault from './components/client/Default.vue';
import ClientContent from './components/client/Content.vue';
import ClientContentWithLeftMenu from './components/client/ContentWithLeftMenu.vue';
import ClientHome from './components/client/Home.vue';
import ClientNotFound from './components/client/NotFound.vue';

// ── Component imports: Misc / utility (kept static — used across pages) ───────
import LoginForm from './components/LoginForm.vue';
import TopMenu from './components/misc/TopMenu.vue';
import NewsHeadlines from './components/misc/NewsHeadlines.vue';
import ItemList from './components/misc/ItemList.vue';
import MiscNotFound from './components/misc/NotFound.vue';
import MultiCheckbox from './components/misc/MultiCheckbox.vue';
import DisplayMessage from './components/misc/DisplayMessage.vue';

// ── Component imports: Subform (kept static) ──────────────────────────────────
// Subforms are nested inside the big page-level forms. When the parent
// form's chunk is downloaded, Rollup will inline these into the same
// chunk anyway (they're imported via the .vue file's own <script>
// imports), so making them async at the top level here would only add
// noise. Easier to leave them static and let Rollup decide.
import AddNewAddress from './components/subform/AddNewAddress.vue';
import UpdateContact from './components/subform/UpdateContact.vue';
import UpdateOfficer from './components/subform/UpdateOfficer.vue';
import UpdateShareholder from './components/subform/UpdateShareholder.vue';
import UpdateRegisterCompanyParty from './components/subform/UpdateRegisterCompanyParty.vue';
import UpdateAddress from './components/subform/UpdateAddress.vue';
import UpdateParty from './components/subform/UpdateParty.vue';
import UpdateTrustParty from './components/subform/UpdateTrustParty.vue';
import UpdateDocumentBrand from './components/subform/UpdateDocumentBrand.vue';
import UpdateImage from './components/subform/UpdateImage.vue';
import UpdateSubItem from './components/subform/UpdateSubItem.vue';
import UpdateFteReviewEntity from './components/subform/UpdateFteReviewEntity.vue';

// ── Async chunk helper ────────────────────────────────────────────────────────
// Thin wrapper around defineAsyncComponent so the registration table
// below stays readable. No suspense / error boundary — the existing
// Vue error handler at the bottom of this file catches load failures,
// and a brief blank frame while a chunk loads is acceptable UX
// (almost always sub-100ms on a warm cache).
const lazy = (loader) => defineAsyncComponent(loader);


/**
 * Global constants
 */
var global_constant = {
    weekday: [
        'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday',
        'Friday', 'Saturday'
    ],
    weekday_short: [
        'Su', 'Mo', 'Tu', 'We', 'Tr', 'Fr', 'Sa'
    ],
    month: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ],
    month_short: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ]
};

/**
 * Date formatting utility function.
 * In Vue 2 this was a global filter; Vue 3 removed filters.
 * Now registered as app.config.globalProperties.$dateFormat
 * Usage in templates: $dateFormat(date, format)
 * Usage in JS: this.$dateFormat(date, format) or this.$root.$dateFormat(date, format)
 */
function dateFormat(date_input, format) {
    if (typeof date_input === 'undefined') {
        date_input = new Date();
    }
    if (typeof date_input === 'string') {
        if (!date_input.match(/Z$/)) {
            // Convert date string to Zulu Time; Note: add ' GMT' instead of 'Z' works for chrome as well, but broke in Firefox
            date_input += 'Z';
        }
        date_input = new Date(date_input);
    }
    if (typeof format === 'undefined') {
        format = 'DD MMM, YYYY HH:mm:ss';
    }

    var padZeros = function (input, maxLength) {
        if (maxLength === void 0) { maxLength = 0; }
        return ("0000" + input).slice(-maxLength);
    };

    var year = date_input.getFullYear();
    var month = date_input.getMonth() + 1;
    var date = date_input.getDate();
    var hours24 = date_input.getHours();
    var hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    var minutes = date_input.getMinutes();
    var seconds = date_input.getSeconds();
    var weekday = date_input.getDay();
    return format
        // Normalize tokens
        .replace('YYYY', '%01%')
        .replace('YY', '%02%')
        .replace('MMMM', '%03%')
        .replace('MMM', '%04%')
        .replace('MM', '%05%')
        .replace('M', '%06%')
        .replace('DD', '%07%')
        .replace('D', '%08%')
        .replace('HH', '%09%')
        .replace('H', '%10%')
        .replace('hh', '%11%')
        .replace('h', '%12%')
        .replace('mm', '%13%')
        .replace('m', '%14%')
        .replace('ss', '%15%')
        .replace('s', '%16%')
        .replace('A', '%17%')
        .replace('a', '%18%')
        .replace('dddd', '%19%')
        .replace('dd', '%20%')
        .replace('d', '%21%')
        // Insert values
        .replace('%01%', padZeros(year, 4))
        .replace('%02%', padZeros(year % 100, 2))
        .replace('%03%', global_constant.month[month - 1])
        .replace('%04%', global_constant.month_short[month - 1])
        .replace('%05%', padZeros(month, 2))
        .replace('%06%', "" + month)
        .replace('%07%', padZeros(date, 2))
        .replace('%08%', "" + date)
        .replace('%09%', padZeros(hours24, 2))
        .replace('%10%', "" + hours24)
        .replace('%11%', padZeros(hours12, 2))
        .replace('%12%', "" + hours12)
        .replace('%13%', padZeros(minutes, 2))
        .replace('%14%', "" + minutes)
        .replace('%15%', padZeros(seconds, 2))
        .replace('%16%', "" + seconds)
        .replace('%17%', hours24 < 12 ? 'AM' : 'PM')
        .replace('%18%', hours24 < 12 ? 'am' : 'pm')
        .replace('%19%', global_constant.weekday[weekday])
        .replace('%20%', global_constant.weekday_short[weekday])
        .replace('%21%', "" + weekday);
}

// ── Vue 3 Application ──────────────────────────────────────────────────────

const appOptions = {
    mounted() {
        let current_object = this;

        // Track whether the page was just restored from bfcache — popstate
        // may fire right after pageshow, and we want to skip cross-document
        // reloads when bfcache has already handled the restore.
        let justRestoredFromBfcache = false;
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                // bfcache restore: clear any stuck loading state
                current_object.flag_loading = 0;
                justRestoredFromBfcache = true;
                // Clear the flag after a short delay — only the immediate
                // popstate should be suppressed, not future ones.
                setTimeout(() => { justRestoredFromBfcache = false; }, 100);
            }
        });

        window.addEventListener('popstate', (event)=>{
            // If the URL isn't in this app's route map, the URL belongs to a
            // different blade template (e.g. the AML SPA at /client/aml/*).
            if (!current_object.item_map['uri_pathname'][location.pathname]) {
                // If bfcache just restored the correct document, no reload needed.
                if (justRestoredFromBfcache) {
                    return;
                }
                // If the current document already matches the URL type
                // (e.g. body has aml_panel and URL is /client/aml/*), it's
                // already correct — no reload needed.
                // /demo and /demo/* count as AML URLs since the demo blade
                // mounts the same AML panel (with body class aml_panel).
                let isAmlUrl = location.pathname.indexOf('/client/aml') === 0
                    || location.pathname === '/demo'
                    || location.pathname.indexOf('/demo/') === 0;
                let isAmlDoc = document.body.classList.contains('aml_panel');
                if (isAmlUrl === isAmlDoc) {
                    return;
                }
                // Document and URL mismatch — must load the correct page.
                window.location.reload();
                return;
            }

            current_object.flag_loading++;
            current_object.disable_watch = true;
            try {
                current_object.set_page(current_object.item_map['uri_pathname'][location.pathname].id,location.search,{update_state:false})
            } catch (e) {
                console.error('[popstate] routing error:', e);
            } finally {
                current_object.disable_watch = false;
                current_object.flag_loading--;
            }

            // host: "www.macquariegs.com.au"
            // hostname: "www.macquariegs.com.au"
            // href: "https://www.macquariegs.com.au/admin/companies?v=1&order=name"
            // origin: "https://www.macquariegs.com.au"
            // pathname: "/admin/companies"
            // port: ""
            // protocol: "https:"
            // search: "?v=1&order=name"

            // if (!event.state) {
            //     // If previously pushed history item does not have a state, try to decode from uri query
            //     let regex_query = new RegExp('([^?]*)[?]([^#]*)'),
            //         results = regex_query.exec(location.href);
            //     if (results && results[2]) {
            //         let search = results[2];
            //         current_object.uri_query = JSON.parse('{"' + search.replace(/&/g, '","').replace(/=/g,'":"') + '"}', function(key, value) { return key===""?value:decodeURIComponent(value) })
            //     } else {
            //         current_object.uri_query = {};
            //     }
            // } else {
            //     current_object.current_tab = current_object.tab_name_map[event.state.tab];
            //     if (!current_object.current_tab) {
            //         if (current_object.tabs && current_object.tabs.length) {
            //             current_object.current_tab = current_object.tabs[0];
            //         }
            //     }
            //     current_object.current_video = false;
            //     if (event.state.query) {
            //         current_object.uri_query = event.state.query;
            //     } else {
            //         current_object.uri_query = {};
            //     }
            // }
        });
        // next_available: options.next_available?options.next_available:{},
        //     display_login_form: options.user?false:true,
        //     user: options.user?options.user:null,
        //     message: options.message?options.message:'',
        //     form_id: options.form_id?options.form_id:'general_login',
        //     username: options.username?options.username:'',
        //     password: '',
        //     remember: options.remember?true:false
    },
    data() {
      try {
        let data_set = {};
        let app_root = document.querySelector('#app');
        let current_object = this;

        // Vue 3: mount() clears container innerHTML before calling data(),
        // so we read the init data before mount() and store it in window.__initData
        let initDataStr = window.__initData;
        if (initDataStr) {
            data_set = Object.assign(data_set,JSON.parse(window.atob(initDataStr)));
console.log('allen debug init data',data_set);
            if (typeof data_set !== 'object' || typeof data_set.length !== 'undefined') {
                data_set = {
                    message_option: {
                        message_type: 'error',
                        message: 'Init Error, Failed to load init data'
                    },
                }
            }
            if (!data_set.message_option) {
                data_set.message_option = {};
            }
            data_set.flag_loading = 0;
            // data_set.loaded = {};

            if (!data_set.current_id) {
                data_set.current_id = {};
            }
            data_set.item_map = {};
            data_set.item_map.page = {};
            data_set.item_map.uri_pathname = {};

            if (!data_set.item_list) {
                data_set.item_list = {};
            }
            if (!data_set.item_list.country) {
                data_set.item_list.country = [
                    {'id':'AF','name':'Afghanistan'},
                    {'id':'AL','name':'Albania'},
                    {'id':'DZ','name':'Algeria'},
                    {'id':'AS','name':'American Samoa'},
                    {'id':'AD','name':'Andorra'},
                    {'id':'AO','name':'Angola'},
                    {'id':'AI','name':'Anguilla'},
                    {'id':'AQ','name':'Antarctica'},
                    {'id':'AG','name':'Antigua and Barbuda'},
                    {'id':'AR','name':'Argentina'},
                    {'id':'AM','name':'Armenia'},
                    {'id':'AW','name':'Aruba'},
                    {'id':'AU','name':'Australia'},
                    {'id':'AT','name':'Austria'},
                    {'id':'AZ','name':'Azerbaijan'},
                    {'id':'BS','name':'Bahamas'},
                    {'id':'BH','name':'Bahrain'},
                    {'id':'BD','name':'Bangladesh'},
                    {'id':'BB','name':'Barbados'},
                    {'id':'BY','name':'Belarus'},
                    {'id':'BE','name':'Belgium'},
                    {'id':'BZ','name':'Belize'},
                    {'id':'BJ','name':'Benin'},
                    {'id':'BM','name':'Bermuda'},
                    {'id':'BT','name':'Bhutan'},
                    {'id':'BO','name':'Bolivia, Plurinational State of'},
                    {'id':'BO','name':'Bolivia'},
                    {'id':'BA','name':'Bosnia and Herzegovina'},
                    {'id':'BW','name':'Botswana'},
                    {'id':'BV','name':'Bouvet Island'},
                    {'id':'BR','name':'Brazil'},
                    {'id':'IO','name':'British Indian Ocean Territory'},
                    {'id':'BN','name':'Brunei Darussalam'},
                    {'id':'BN','name':'Brunei'},
                    {'id':'BG','name':'Bulgaria'},
                    {'id':'BF','name':'Burkina Faso'},
                    {'id':'BI','name':'Burundi'},
                    {'id':'KH','name':'Cambodia'},
                    {'id':'CM','name':'Cameroon'},
                    {'id':'CA','name':'Canada'},
                    {'id':'CV','name':'Cape Verde'},
                    {'id':'KY','name':'Cayman Islands'},
                    {'id':'CF','name':'Central African Republic'},
                    {'id':'TD','name':'Chad'},
                    {'id':'CL','name':'Chile'},
                    {'id':'CN','name':'China'},
                    {'id':'CX','name':'Christmas Island'},
                    {'id':'CC','name':'Cocos (Keeling) Islands'},
                    {'id':'CO','name':'Colombia'},
                    {'id':'KM','name':'Comoros'},
                    {'id':'CG','name':'Congo'},
                    {'id':'CD','name':'Congo, the Democratic Republic of the'},
                    {'id':'CK','name':'Cook Islands'},
                    {'id':'CR','name':'Costa Rica'},
                    {'id':'CI','name':'Côte d\'Ivoire'},
                    {'id':'CI','name':'Ivory Coast'},
                    {'id':'HR','name':'Croatia'},
                    {'id':'CU','name':'Cuba'},
                    {'id':'CY','name':'Cyprus'},
                    {'id':'CZ','name':'Czech Republic'},
                    {'id':'DK','name':'Denmark'},
                    {'id':'DJ','name':'Djibouti'},
                    {'id':'DM','name':'Dominica'},
                    {'id':'DO','name':'Dominican Republic'},
                    {'id':'EC','name':'Ecuador'},
                    {'id':'EG','name':'Egypt'},
                    {'id':'SV','name':'El Salvador'},
                    {'id':'GQ','name':'Equatorial Guinea'},
                    {'id':'ER','name':'Eritrea'},
                    {'id':'EE','name':'Estonia'},
                    {'id':'ET','name':'Ethiopia'},
                    {'id':'FK','name':'Falkland Islands (Malvinas)'},
                    {'id':'FO','name':'Faroe Islands'},
                    {'id':'FJ','name':'Fiji'},
                    {'id':'FI','name':'Finland'},
                    {'id':'FR','name':'France'},
                    {'id':'GF','name':'French Guiana'},
                    {'id':'PF','name':'French Polynesia'},
                    {'id':'TF','name':'French Southern Territories'},
                    {'id':'GA','name':'Gabon'},
                    {'id':'GM','name':'Gambia'},
                    {'id':'GE','name':'Georgia'},
                    {'id':'DE','name':'Germany'},
                    {'id':'GH','name':'Ghana'},
                    {'id':'GI','name':'Gibraltar'},
                    {'id':'GR','name':'Greece'},
                    {'id':'GL','name':'Greenland'},
                    {'id':'GD','name':'Grenada'},
                    {'id':'GP','name':'Guadeloupe'},
                    {'id':'GU','name':'Guam'},
                    {'id':'GT','name':'Guatemala'},
                    {'id':'GG','name':'Guernsey'},
                    {'id':'GN','name':'Guinea'},
                    {'id':'GW','name':'Guinea-Bissau'},
                    {'id':'GY','name':'Guyana'},
                    {'id':'HT','name':'Haiti'},
                    {'id':'HM','name':'Heard Island and McDonald Islands'},
                    {'id':'VA','name':'Holy See (Vatican City State)'},
                    {'id':'HN','name':'Honduras'},
                    {'id':'HK','name':'Hong Kong'},
                    {'id':'HU','name':'Hungary'},
                    {'id':'IS','name':'Iceland'},
                    {'id':'IN','name':'India'},
                    {'id':'ID','name':'Indonesia'},
                    {'id':'IR','name':'Iran, Islamic Republic of'},
                    {'id':'IQ','name':'Iraq'},
                    {'id':'IE','name':'Ireland'},
                    {'id':'IM','name':'Isle of Man'},
                    {'id':'IL','name':'Israel'},
                    {'id':'IT','name':'Italy'},
                    {'id':'JM','name':'Jamaica'},
                    {'id':'JP','name':'Japan'},
                    {'id':'JE','name':'Jersey'},
                    {'id':'JO','name':'Jordan'},
                    {'id':'KZ','name':'Kazakhstan'},
                    {'id':'KE','name':'Kenya'},
                    {'id':'KI','name':'Kiribati'},
                    {'id':'KP','name':'Korea, Democratic People\'s Republic of'},
                    {'id':'KR','name':'Korea, Republic of'},
                    {'id':'KR','name':'South Korea'},
                    {'id':'KW','name':'Kuwait'},
                    {'id':'KG','name':'Kyrgyzstan'},
                    {'id':'LA','name':'Lao People\'s Democratic Republic'},
                    {'id':'LV','name':'Latvia'},
                    {'id':'LB','name':'Lebanon'},
                    {'id':'LS','name':'Lesotho'},
                    {'id':'LR','name':'Liberia'},
                    {'id':'LY','name':'Libyan Arab Jamahiriya'},
                    {'id':'LY','name':'Libya'},
                    {'id':'LI','name':'Liechtenstein'},
                    {'id':'LT','name':'Lithuania'},
                    {'id':'LU','name':'Luxembourg'},
                    {'id':'MO','name':'Macao'},
                    {'id':'MK','name':'Macedonia, the former Yugoslav Republic of'},
                    {'id':'MG','name':'Madagascar'},
                    {'id':'MW','name':'Malawi'},
                    {'id':'MY','name':'Malaysia'},
                    {'id':'MV','name':'Maldives'},
                    {'id':'ML','name':'Mali'},
                    {'id':'MT','name':'Malta'},
                    {'id':'MH','name':'Marshall Islands'},
                    {'id':'MQ','name':'Martinique'},
                    {'id':'MR','name':'Mauritania'},
                    {'id':'MU','name':'Mauritius'},
                    {'id':'YT','name':'Mayotte'},
                    {'id':'MX','name':'Mexico'},
                    {'id':'FM','name':'Micronesia, Federated States of'},
                    {'id':'MD','name':'Moldova, Republic of'},
                    {'id':'MC','name':'Monaco'},
                    {'id':'MN','name':'Mongolia'},
                    {'id':'ME','name':'Montenegro'},
                    {'id':'MS','name':'Montserrat'},
                    {'id':'MA','name':'Morocco'},
                    {'id':'MZ','name':'Mozambique'},
                    {'id':'MM','name':'Myanmar'},
                    {'id':'MM','name':'Burma'},
                    {'id':'NA','name':'Namibia'},
                    {'id':'NR','name':'Nauru'},
                    {'id':'NP','name':'Nepal'},
                    {'id':'NL','name':'Netherlands'},
                    {'id':'AN','name':'Netherlands Antilles'},
                    {'id':'NC','name':'New Caledonia'},
                    {'id':'NZ','name':'New Zealand'},
                    {'id':'NI','name':'Nicaragua'},
                    {'id':'NE','name':'Niger'},
                    {'id':'NG','name':'Nigeria'},
                    {'id':'NU','name':'Niue'},
                    {'id':'NF','name':'Norfolk Island'},
                    {'id':'MP','name':'Northern Mariana Islands'},
                    {'id':'NO','name':'Norway'},
                    {'id':'OM','name':'Oman'},
                    {'id':'PK','name':'Pakistan'},
                    {'id':'PW','name':'Palau'},
                    {'id':'PS','name':'Palestinian Territory, Occupied'},
                    {'id':'PA','name':'Panama'},
                    {'id':'PG','name':'Papua New Guinea'},
                    {'id':'PY','name':'Paraguay'},
                    {'id':'PE','name':'Peru'},
                    {'id':'PH','name':'Philippines'},
                    {'id':'PN','name':'Pitcairn'},
                    {'id':'PL','name':'Poland'},
                    {'id':'PT','name':'Portugal'},
                    {'id':'PR','name':'Puerto Rico'},
                    {'id':'QA','name':'Qatar'},
                    {'id':'RE','name':'Réunion'},
                    {'id':'RO','name':'Romania'},
                    {'id':'RU','name':'Russian Federation'},
                    {'id':'RU','name':'Russia'},
                    {'id':'RW','name':'Rwanda'},
                    {'id':'SH','name':'Saint Helena, Ascension and Tristan da Cunha'},
                    {'id':'KN','name':'Saint Kitts and Nevis'},
                    {'id':'LC','name':'Saint Lucia'},
                    {'id':'PM','name':'Saint Pierre and Miquelon'},
                    {'id':'VC','name':'Saint Vincent and the Grenadines'},
                    {'id':'VC','name':'Saint Vincent & the Grenadines'},
                    {'id':'VC','name':'St. Vincent and the Grenadines'},
                    {'id':'WS','name':'Samoa'},
                    {'id':'SM','name':'San Marino'},
                    {'id':'ST','name':'Sao Tome and Principe'},
                    {'id':'SA','name':'Saudi Arabia'},
                    {'id':'SN','name':'Senegal'},
                    {'id':'RS','name':'Serbia'},
                    {'id':'SC','name':'Seychelles'},
                    {'id':'SL','name':'Sierra Leone'},
                    {'id':'SG','name':'Singapore'},
                    {'id':'SK','name':'Slovakia'},
                    {'id':'SI','name':'Slovenia'},
                    {'id':'SB','name':'Solomon Islands'},
                    {'id':'SO','name':'Somalia'},
                    {'id':'ZA','name':'South Africa'},
                    {'id':'GS','name':'South Georgia and the South Sandwich Islands'},
                    {'id':'SS','name':'South Sudan'},
                    {'id':'ES','name':'Spain'},
                    {'id':'LK','name':'Sri Lanka'},
                    {'id':'SD','name':'Sudan'},
                    {'id':'SR','name':'Suriname'},
                    {'id':'SJ','name':'Svalbard and Jan Mayen'},
                    {'id':'SZ','name':'Swaziland'},
                    {'id':'SE','name':'Sweden'},
                    {'id':'CH','name':'Switzerland'},
                    {'id':'SY','name':'Syrian Arab Republic'},
                    {'id':'TW','name':'Taiwan, Province of China'},
                    {'id':'TW','name':'Taiwan'},
                    {'id':'TJ','name':'Tajikistan'},
                    {'id':'TZ','name':'Tanzania, United Republic of'},
                    {'id':'TH','name':'Thailand'},
                    {'id':'TL','name':'Timor-Leste'},
                    {'id':'TG','name':'Togo'},
                    {'id':'TK','name':'Tokelau'},
                    {'id':'TO','name':'Tonga'},
                    {'id':'TT','name':'Trinidad and Tobago'},
                    {'id':'TN','name':'Tunisia'},
                    {'id':'TR','name':'Turkey'},
                    {'id':'TM','name':'Turkmenistan'},
                    {'id':'TC','name':'Turks and Caicos Islands'},
                    {'id':'TV','name':'Tuvalu'},
                    {'id':'UG','name':'Uganda'},
                    {'id':'UA','name':'Ukraine'},
                    {'id':'AE','name':'United Arab Emirates'},
                    {'id':'GB','name':'United Kingdom'},
                    {'id':'US','name':'United States'},
                    {'id':'UM','name':'United States Minor Outlying Islands'},
                    {'id':'UY','name':'Uruguay'},
                    {'id':'UZ','name':'Uzbekistan'},
                    {'id':'VU','name':'Vanuatu'},
                    {'id':'VE','name':'Venezuela, Bolivarian Republic of'},
                    {'id':'VE','name':'Venezuela'},
                    {'id':'VN','name':'Viet Nam'},
                    {'id':'VN','name':'Vietnam'},
                    {'id':'VG','name':'Virgin Islands, British'},
                    {'id':'VI','name':'Virgin Islands, U.S.'},
                    {'id':'WF','name':'Wallis and Futuna'},
                    {'id':'EH','name':'Western Sahara'},
                    {'id':'YE','name':'Yemen'},
                    {'id':'ZM','name':'Zambia'},
                    {'id':'ZW','name':'Zimbabwe'},
                ];
            }

            // set up field from initial data if provided
            if (!data_set.item_field) {
                data_set.item_field = {};
            } else {
// console.log('allen debug data_set init item_field',data_set.item_field);
                Object.keys(data_set.item_field).forEach(function (module_name) {
// console.log(module_name);
                    current_object.set_field(data_set.item_field[module_name], data_set.item_field[module_name]);
                });
            }

            let source_item_list = data_set.item_list;
            data_set.item_list = {};
            current_object.set_item_list(source_item_list, data_set);
            // if (data_set.item_map.page && data_set.item_map.sub_page) {
            //     data_set.item_map.page = Object.assign(data_set.item_map.page,data_set.item_map.sub_page);
            // }

            // current_object.set_item_list_map(data_set.item_list, data_set);
            // current_object.set_item_list_field(data_set.item_list, data_set);
            // current_object.set_item_list_relationship(data_set.item_list, data_set);


            // Object.keys(data_set.item_list).forEach(function (item_name) {
            //     if (data_set.item_list[item_name].length > 0) {
            //         current_object.set_relationship(data_set.item_list[item_name], data_set.item_map, item_name, data_set.item_field[item_name]);
            //     }
            // };
            // console.log('allen debug data_set.item_field 2',Object.keys(data_set.item_field));

            // if (data_set.item_list.menu) {
            //     data_set.item_list.menu = _.orderBy(data_set.item_list.menu, 'display_order', 'asc');
            // }
            if (data_set.item_list.stored_party) {
                data_set.item_list.stored_party = _.orderBy(data_set.item_list.stored_party, '_order_formatted_name', 'asc');
            }
            if (data_set.item_list.user) {
                data_set.item_list.user = _.orderBy(data_set.item_list.user, '_order_long_name', 'asc');
            }
            if (data_set.item_list.company) {
                data_set.item_list.company = _.orderBy(data_set.item_list.company, '_order_name', 'asc');
            }

            // Link current_user to the item_map version so it has _ref_company,
            // company_admin, and other relationship properties set by set_module_relationship
            if (data_set.current_user && data_set.item_map && data_set.item_map['user']) {
                let mapped_user = data_set.item_map['user'][data_set.current_user.id];
                if (mapped_user) {
                    data_set.current_user = mapped_user;
                }
            }

            if (data_set.loaded) {
                Object.keys(data_set.loaded).forEach(function (item_name) {
                    if (data_set.loaded[item_name] === 'now') {
                        data_set.loaded[item_name] = new Date();
                    } else {
                        if (typeof data_set.loaded[item_name] === 'string') {
                            data_set.loaded[item_name] = new Date(data_set.loaded[item_name]);
                        }
                    }
                });
            } else {
                data_set.loaded = {};
            }
        }

        if (window.__appRootClasses.indexOf('login_form_app') !== -1) {
            data_set.form_options = {
                next_available: data_set.next_available?data_set.next_available:{},
                display_login_form: data_set.current_user?false:true,
                current_user: data_set.current_user?data_set.current_user:null,
                message_type: '',
                message: data_set.message?data_set.message:'',
                form_id: data_set.form_id?data_set.form_id:'user_login',
                username: data_set.username?data_set.username:(data_set.current_user?data_set.current_user.name:''),
                password: '',
                remember: data_set.remember?true:false
            };
            // data_set.current_user = data_set.user;
            if (data_set.status) {
                switch (data_set.status) {
                    case 'OK':
                        data_set.form_options.message_type = 'success';
                        break;
                    case 'PERMISSION_DENIED':
                        data_set.form_options.message_type = 'warning';
                        break;
                    default:
                        data_set.form_options.message_type = 'error';
                }
            }
        }
        data_set.popstate = {};
        if (typeof data_set.panel === 'undefined') {
            data_set.panel = 'client';
        }
        let base_uri = data_set.panel;
        if (data_set.base_uri) {
            base_uri = data_set.base_uri;
        } else {
            data_set.base_uri = base_uri;
        }
console.log('[DEBUG] checkpoint A - before page setup', typeof data_set.item_map, data_set.item_map);
        // Set page item uri_pathname and component
        if (data_set.item_list['page']) {
            data_set.item_map['uri_pathname'] = {};
            data_set.item_list['page'].forEach(function (item) {
                item.pathname = '/'+base_uri+'/'+item.name;
                data_set.item_map['uri_pathname'][item.pathname] = item;


            });
            if (data_set.panel === 'client') {
                // let action_list = ['create','view'];
                let module_list = ['loan_agreement','gift_deed','trust_package','trust_package_bmdtpk','trust_package_bnptpk'];
                module_list.forEach(function (module) {
                    // For dynamic modules, add list page
                    let list_page_item = {
                        component_name: 'item-list',
                        list_name: module,
                        id: 'client_item_list_' + module,
                        name: 'my-'+module.replaceAll('_','-')+'s',
                        pathname: '/'+base_uri+'/my-' + module.replaceAll('_','-')+'s',
                        description: '',
                        content: '',
                        updated_at: new Date(),
                        created_at: new Date(),
                        display_name: ('My '+module.replace('_',' ')+' Orders').toLowerCase().replace(/\b[a-z]/g, function(letter) {
                            return letter.toUpperCase();
                        }),
                        type: 'vue_component',
                        // fields: ['id','name','user_id','created_at','updated_at'],
                        // list_controllers: ['order_completed','order_pending','add','edit','attach','view','preview','download','delete'],
                    };
                    if (!data_set.item_map['uri_pathname'][list_page_item.pathname]) {
                        data_set.item_list['page'].push(list_page_item);
                        data_set.item_map['page'][list_page_item.id] = list_page_item;
                        data_set.item_map['uri_pathname'][list_page_item.pathname] = list_page_item;
                    }

                    let form_page_item = {
                        component_name: 'form-update-item',
                        module_name: module,
                        component_action: 'create',
                        id: 'client-create-' + module.replaceAll('_','-'),
                        name: 'order-' + module.replaceAll('_','-'),
                        pathname: '/'+base_uri+'/order-' + module.replaceAll('_','-'),
                        description: '',
                        content: '',
                        updated_at: new Date(),
                        created_at: new Date(),
                        display_name: ('Order ' + module.replace('_',' ')).toLowerCase().replace(/\b[a-z]/g, function(letter) {
                            return letter.toUpperCase();
                        }),
                        type: 'vue_component'
                    };
                    if (!data_set.item_map['uri_pathname'][form_page_item.pathname]) {
                        data_set.item_list['page'].push(form_page_item);
                        data_set.item_map['page'][form_page_item.id] = form_page_item;
                        data_set.item_map['uri_pathname'][form_page_item.pathname] = form_page_item;
                    }

                    // For dynamic modules, add view page
                    let view_page_item = {
                        component_name: 'form-update-item',
                        module_name: module,
                        component_action: 'view',
                        id: 'client-view-' + module.replaceAll('_','-'),
                        name: 'view-' + module.replaceAll('_','-'),
                        pathname: '/'+base_uri+'/view-' + module.replaceAll('_','-'),
                        description: '',
                        content: '',
                        updated_at: new Date(),
                        created_at: new Date(),
                        display_name: ('View ' + module.replace('_',' ')).toLowerCase().replace(/\b[a-z]/g, function(letter) {
                            return letter.toUpperCase();
                        }),
                        type: 'vue_component'
                    };
                    if (!data_set.item_map['uri_pathname'][view_page_item.pathname]) {
                        data_set.item_list['page'].push(view_page_item);
                        data_set.item_map['page'][view_page_item.id] = view_page_item;
                        data_set.item_map['uri_pathname'][view_page_item.pathname] = view_page_item;
                    }

                });
            }
            if (data_set.panel === 'admin') {
                let action_list = ['update','view'];
                let module_list = ['loan_agreement','gift_deed','fte_review','custom_theme','custom_page','fte_review','trust_package','trust_package_bmdtpk','trust_package_bnptpk'];
                module_list.forEach(function (module) {
                    // For dynamic modules, add list page
                    let page_item = {
                        component_name: 'item-list',
                        list_name: module,
                        id: 'item_list_' + module,
                        name: module.replaceAll('_','-')+'s',
                        pathname: '/'+base_uri+'/' + module.replaceAll('_','-')+'s',
                        description: '',
                        content: '',
                        updated_at: new Date(),
                        created_at: new Date(),
                        display_name: (module.replace('_',' ')+' Orders').toLowerCase().replace(/\b[a-z]/g, function(letter) {
                            return letter.toUpperCase();
                        }),
                        type: 'vue_component',
                        // fields: ['id','name','user_id','created_at','updated_at'],
                        // list_controllers: ['order_completed','order_pending','add','edit','attach','view','preview','download','delete'],
                    };
                    if (!data_set.item_map['uri_pathname'][page_item.pathname]) {
                        data_set.item_list['page'].push(page_item);
                        data_set.item_map['page'][page_item.id] = page_item;
                        data_set.item_map['uri_pathname'][page_item.pathname] = page_item;
                    }
                    // For dynamic modules, add form pages
                    action_list.forEach(function (action) {
                        let page_item = {
                            component_name: 'form-update-item',
                            module_name: module,
                            component_action: action,
                            id: 'update_form_'+action + '-' + module.replaceAll('_','-'),
                            name: action + '-' + module.replaceAll('_','-'),
                            pathname: '/'+base_uri+'/' + action + '-' + module.replaceAll('_','-'),
                            description: '',
                            content: '',
                            updated_at: new Date(),
                            created_at: new Date(),
                            display_name: (action + ' ' + module.replace('_',' ')).toLowerCase().replace(/\b[a-z]/g, function(letter) {
                                return letter.toUpperCase();
                            }),
                            type: 'vue_component'
                        };
                        if (!data_set.item_map['uri_pathname'][page_item.pathname]) {
                            data_set.item_list['page'].push(page_item);
                            data_set.item_map['page'][page_item.id] = page_item;
                            data_set.item_map['uri_pathname'][page_item.pathname] = page_item;
                        }
                    });
                });

                // Fix menu items with missing page_id by matching to dynamically created list pages
                if (data_set.item_list['menu']) {
                    data_set.item_list['menu'].forEach(function (menu_item) {
                        if (!menu_item.page_id && menu_item.display_name) {
                            // Try to find a matching dynamically created list page by pathname
                            let menu_name = menu_item.display_name.toLowerCase().replace(/\s+/g, '-');
                            let expected_pathname = '/'+base_uri+'/' + menu_name;
                            let matched_page = data_set.item_map['uri_pathname'][expected_pathname];
                            if (matched_page) {
                                menu_item.page_id = matched_page.id;
                            }
                        }
                    });
                }
            }
            data_set.item_list['page'].forEach(function (item) {
                switch (item.pathname) {
                    case '/profile/create':
                        item.component_name = 'client-profile-form';
                        break;
                    case '/admin/fte-reviews':
                        item.component_name = 'item-list';
                        item.list_name = 'fte_review';
                        item.request_module = ['fte_review','user'];
                        // item.list_field = ['id','type','name','user_id','created_at','updated_at'];
                        item.list_field = [{
                            name:'id',
                            display_name:'ID'
                        },{
                            name:'name',
                            display_name:'Name'
                        },{
                            name:'order_status',
                            display_name:'Status'
                        },'user_id','company_id','created_at','updated_at'];
                        item.list_controllers = ['order_completed','order_pending','add','edit','diagram','delete'];
                        item.order_by = 'id';
                        item.order_seq = 'desc';
                        break;
                    case '/admin/gift-deeds':
                        item.request_module = ['gift_deed','user'];
                        item.list_field = [{
                            name:'id',
                            display_name:'ID'
                        },'name','user_id','company_id','created_at','updated_at'];
                        item.list_controllers = ['order_completed','order_pending','add','edit','attach','view','preview','download','download_docx','delete'];
                        item.order_by = 'id';
                        item.order_seq = 'desc';
                        break;
                    case '/admin/loan-agreements':
                        item.component_name = 'item-list';
                        item.list_name = 'loan_agreement';
                        item.request_module = ['loan_agreement','user'];
                        // item.list_field = ['id','type','name','user_id','created_at','updated_at'];
                        item.list_field = [{
                            name:'id',
                            display_name:'ID'
                        },'name',{
                            name:'type',
                            display_name:'Type'
                        },'variation','user_id','company_id','created_at','updated_at'];
                        item.list_controllers = ['order_completed','order_pending','add','edit','attach','view','preview','download','download_docx','delete'];
                        item.order_by = 'id';
                        item.order_seq = 'desc';
                        break;
                    case '/admin/supers':
                        item.component_name = 'item-list';
                        item.list_name = 'super';
                        item.request_module = ['super','user'];
                        // item.list_field = ['id','type','name','user_id','created_at','updated_at'];
                        item.list_field = [{
                            name:'id',
                            display_name:'ID'
                        },{
                            name:'type',
                            display_name:'Type'
                        },'name',{
                            name:'additional_copy',
                            display_name:'Copies',
                        },'user_id','company_id','created_at','updated_at'];
                        item.list_controllers = ['order_completed','order_pending','add','edit','attach','view','preview','download','download_docx','delete'];
                        item.order_by = 'id';
                        item.order_seq = 'desc';
                        break;
                    case '/admin/trusts':
                        item.component_name = 'item-list';
                        item.list_name = 'trust';
                        item.request_module = ['trust','user','company'];
                        // item.list_field = ['id','type','name','user_id','created_at','updated_at'];
                        item.list_field = [{
                            name:'id',
                            display_name:'ID'
                        },{
                            name:'type',
                            display_name:'Type'
                        },{
                            name:'variation',
                            display_name:'Variation'
                        },'name',{
                            name:'additional_copy',
                            display_name:'Copies',
                        },'user_id','company_id','created_at','updated_at'];
                        item.list_controllers = ['order_completed','order_pending','add','edit','attach','view','preview','download','download_docx','delete'];
                        item.order_by = 'id';
                        item.order_seq = 'desc';
                        break;
                    case '/admin/users':
                        item.component_name = 'item-list';
                        item.list_name = 'user';
                        item.request_module = ['address','company','user'];
                        // item.list_field = ['id','type','name','user_id','created_at','updated_at'];
                        item.list_field = ['id',{name:'formatted_name',display_name:'Name'},'email','company_id','created_at','updated_at'];
                        item.list_controllers = ['add','edit','delete','restore'];
                        item.list_field_style = {
                            'lg':{
                                'id':'{width: 5%;}',
                                'formatted_name':'{width: 15%;}',
                                'email':'{width: 15%;}',
                                'company_id':'{width: 15%;}',
                                'company_admin':'{width: 5%;}'
                            }
                        };
                        item.order_by = 'id';
                        item.order_seq = 'desc';
                        break;
                    case '/admin/companies':
                        item.component_name = 'item-list';
                        item.list_name = 'company';
                        item.request_module = ['address','company','user'];
                        // item.list_field = ['id','type','name','user_id','created_at','updated_at'];
                        item.list_field = ['id','name','address_id','created_at','updated_at'];
                        item.list_controllers = ['add','custom_theme_add','custom_theme_edit','edit','delete'];
                        item.order_by = 'id';
                        item.order_seq = 'desc';
                        break;
                    case '/admin/bgl-companies':
                        item.component_name = 'item-list';
                        item.request_module = ['bgl_company','data_attachment'];
                        item.list_name = 'bgl_company';
                        item.list_field = ['id','name','registered','created_at','updated_at','abn'];
                        // item.request_module = ['register_company','register_company_party']
                        // item.list_name = 'register_company';
                        // item.list_field = ['id','name','registered','created_at','updated_at','business_number'];
                        item.order_by = 'created_at';
                        item.order_seq = 'desc';
                        item.list_controllers = [{
                            name: 'add',
                            label: 'Register a New Company'
                        }]; //label:

                        item.list_filter = [
                            {
                                'field':'type',
                                'label':'Filter by Registered',
                                'placeholder':'-- Filter by Registered --',
                                'option':[
                                    {'value':'0','display_name':'Pending'},
                                    {'value':'1','display_name':'Completed'},
                                ]
                            }
                        ];
                        break;
                    case '/admin/register-companies':
                        item.component_name = 'item-list';
                        item.request_module = ['register_company','user'];
                        item.list_name = 'register_company';
                        item.list_field = [{
                            name:'id',
                            display_name:'ID'
                        },'type','name',{
                            name:'additional_copy',
                            display_name:'Copies',
                        },'bgl_status','user_id','company_id','created_at','updated_at'];

                        item.order_by = 'created_at';
                        item.order_seq = 'desc';
                        item.list_controllers = [{
                            name: 'add',
                            label: 'Register a New Company'
                        },'order_completed','order_pending','edit','attach','view','preview','download','download_docx']; //label:

                        break;
                    case '/admin/addresses':
                        item.component_name = 'item-list';
                        item.request_module = ['address'];
                        item.list_name = 'address';
                        item.list_field = ['id','formatted_address','created_at','updated_at'];
                        break;
                    case '/admin/tv-shows':
                        item.component_name = 'item-list';
                        item.list_name = 'tv_show';
                        item.request_module = ['tv_show'];
                        // item.list_field = ['id','type','name','user_id','created_at','updated_at'];
                        item.list_field = ['id',{name:'name',display_name:'Tab'},{name:'description',display_name:'Title'},{name:'display_order',display_name:'Order'},'created_at','updated_at'];
                        item.list_controllers = ['add','edit','delete'];
                        item.list_field_style = {
                            'lg':{
                                'id':'{width: 5%;}',
                                'name':'{width: 15%;}',
                                'description':'{width: 25%;}',
                                'display_order':'{width: 5%;}'
                            }
                        };
                        item.order_by = 'id';
                        item.order_seq = 'desc';
                        break;
                    case '/admin/training-modules':
                        item.component_name = 'manage-training-modules';
                        item.list_name = 'aml_training_module';
                        item.request_module = ['aml_training_module','company'];
                        break;
                    case '/admin/training-module-edit':
                        item.component_name = 'edit-training-module';
                        break;
                    case '/admin/admin-macquarie-tv':
                        item.component_name = 'manage-macquarie-tv';
                        break;
                    case '/admin/disbursements':
                        // Slice 1.5 of Xero/Braintree reconciliation —
                        // admin-only dashboard over braintree_disbursements
                        // captured by the disbursement webhook handler.
                        item.component_name = 'admin-disbursements';
                        break;
                    // case '/admin/view-company':
                    //     item.component_name = 'client-order-company';
                    //     break;
                    case '/'+base_uri+'/order-gift-deed':
                    case '/admin/order-gift-deed':
                    case '/admin/update-gift-deed':
                        item.component_name = 'form-update-item';
                        item.module_name = 'gift_deed';
                        item.component_methods = {
                            get_available_fields: function (data_set) {
                                let current_object = this;
                                if (typeof data_set === 'undefined') {
                                    data_set = current_object;
                                }
                                console.log('allen debug form-order-gift-deed get_available_fields data_set',data_set);
                                let preset_fields = ['name','gift_amount','delivery_method','donor','donee','description'];
                                let optional_fields = ['description'];
                                if (current_object.$root.panel === 'admin') {
                                    preset_fields.splice(5,0,'contact');
                                }
                                // if (['WI','WIW'].indexOf(data_set.cache_data.variation) > -1) {
                                //     preset_fields.splice(3,0,'interest');
                                // }
                                if (current_object.$root.panel === 'admin') {
                                    preset_fields.splice(1,0,'user_id');
                                    optional_fields.splice(0,0,'user_id');
                                }
                                data_set.available_fields.splice(0, data_set.available_fields.length);
                                data_set.mandatory_fields.splice(0, data_set.mandatory_fields.length);
                                preset_fields.forEach(function (field_name) {
                                    if (!data_set.current_item_field[field_name]) {
                                        console.log('Warning: get_available_fields available_fields item does not exist in current_item_field',field_name,Object.keys(data_set.current_item_field));
                                        return false;
                                    }
                                    data_set.available_fields.push(field_name);
                                    if (optional_fields.indexOf(field_name) === -1) {
                                        data_set.mandatory_fields.push(field_name);
                                    }
                                });
                            },
                        };
                        break;
                    // case '/'+base_uri+'/order-gift-deed':
                    //     item.component_name = 'form-update-item';
                    //     item.module_name = 'gift_deed';
                    //     break;
                    case '/'+base_uri+'/order-loan-agreement':
                    case '/admin/order-loan-agreement':
                    case '/admin/update-loan-agreement':
                        item.component_name = 'form-update-item';
                        // item.component_name = 'form-order-loan-agreement';
                        item.module_name = 'loan_agreement';
                        item.component_methods = {
                            get_available_fields: function (data_set) {
                                let current_object = this;
                                if (typeof data_set === 'undefined') {
                                    data_set = current_object;
                                }
                                console.log('allen debug form-order-loan-agreement get_available_fields data_set',data_set);
                                let preset_fields = ['name','loan_purpose','address_id','loan_amount','interest','period','state','delivery_method','np','repayment_date','lender','borrower','description'];
                                let optional_fields = ['np','repayment_date','description'];
                                if (current_object.$root.panel === 'admin') {
                                    preset_fields.splice(9,0,'contact');
                                }
                                // if (['WI','WIW'].indexOf(data_set.cache_data.variation) > -1) {
                                //     preset_fields.splice(3,0,'interest');
                                // }
                                if (current_object.$root.panel === 'admin') {
                                    preset_fields.splice(1,0,'user_id','type','variation');
                                    optional_fields.splice(0,0,'user_id');
                                }
                                data_set.available_fields.splice(0, data_set.available_fields.length);
                                data_set.mandatory_fields.splice(0, data_set.mandatory_fields.length);
                                preset_fields.forEach(function (field_name) {
                                    if (!data_set.current_item_field[field_name]) {
                                        console.log('Warning: get_available_fields available_fields item does not exist in current_item_field',field_name,Object.keys(data_set.current_item_field));
                                        return false;
                                    }
                                    data_set.available_fields.push(field_name);
                                    if (optional_fields.indexOf(field_name) === -1) {
                                        data_set.mandatory_fields.push(field_name);
                                    }
                                });
                            },
                        };
                        break;
                    case '/'+base_uri+'/order-company':
                    case '/admin/order-company':
                        item.component_name = 'form-order-company';
                        break;
                    // case '/'+base_uri+'/order-company':
                    //     item.component_name = 'form-order-company';
                    //     break;
                    case '/admin/order-super':
                    case '/'+base_uri+'/order-super':
                        // case '/'+base_uri+'/edit-super':
                        item.component_name = 'form-order-super';
                        item.module_name = 'super';
                        break;
                    case '/admin/order-trust':
                    case '/'+base_uri+'/order-trust':
                        // item.component_name = 'form-update-item';
                        item.component_name = 'form-order-trust';
                        item.module_name = 'trust';

                        item.component_methods = {
                            get_available_fields: function (data_set) {
                                let current_object = this;
                                if (typeof data_set === 'undefined') {
                                    data_set = current_object;
                                }
                                // let result = [];
                                // switch (current_object.cache_data.type) {
                                //     case 'LTUT':
                                //         result.push('AE');
                                //         break;
                                //     case 'UT':
                                //     case 'WAPUT':
                                //     case 'VICPUT':
                                //         result.splice(0, 0, 'TD', 'PC', 'AE');
                                //         break;
                                //     case 'HUT':
                                //         result.splice(0, 0, 'TD', 'PC');
                                //         break;
                                //     case 'DT':
                                //     case 'HDT':
                                //     case 'CDT':
                                //     case 'CVT':
                                //         result.splice(0, 0, 'C', 'NC');
                                //         break;
                                //     case 'BT':
                                //     case 'ST':
                                //         result.splice(0, 0, 'LD', 'NL1', 'NL', 'SP');
                                //         break;
                                //     default:
                                //         result = Object.keys(current_object.trust_variations);
                                // }
                                // return result;
                            }
                        };
                        break;
                    case '/admin/update-custom-page':
                        item.component_name = 'form-update-item';
                        item.module_name = 'custom_page';
                        break;
                    case '/admin/update-custom-theme':
                        item.component_name = 'form-update-item';
                        item.module_name = 'custom_theme';
                        break;
                    case '/admin/update-document':
                        item.component_name = 'form-update-document';
                        break;
                    case '/admin/update-document-brand':
                        item.component_name = 'form-update-item';
                        item.module_name = 'document_brand';
                        break;
                    case '/admin/update-fte-review':
                        item.component_name = 'form-update-fte-review';
                        item.module_name = 'fte_review';
                        item.component_controllers = ['submit','confirm','revise','cancel'];
                        break;
                    case '/admin/update-news-headlines':
                        item.component_name = 'form-update-news-headlines';
                        break;
                    case '/admin/update-user':
                        // item.component_name = 'form-update-user';
                        // item.component_name = 'form-wrapper';
                        item.component_name = 'form-update-item';
                        item.module_name = 'user';
                        item.component_methods = {
                            get_available_fields: function (data_set) {
                                let current_object = this;
                                if (typeof data_set === 'undefined') {
                                    data_set = current_object;
                                }
                                // console.log('override get_available_fields /admin/update-user', current_object);
                                // 'name' (the username login identifier) is admin-managed only.
                                // The login query falls back to ->where('name',$input), so a user
                                // able to rename themselves could intercept another user's next
                                // login — client panel intentionally omits this field. Server
                                // enforces uniqueness + strips client-supplied 'name' in
                                // PanelController::update_user. Optional on the form: server
                                // defaults name = email when empty on create.
                                let preset_fields = ['company_id', 'name', 'email','title', 'first_name', 'middle_name', 'last_name', 'membership'];
                                let optional_fields = ['company_id', 'name', 'middle_name', 'membership'];
                                if (current_object.cache_data && current_object.cache_data.company_id) {
                                    preset_fields.push('company_admin');
                                } else {
                                    preset_fields.push('address_id');
                                }
                                // console.log('allen debug user form get_available_fields', data_set.component_action);
                                if (data_set.component_action) {
                                    switch (data_set.component_action) {
                                        case 'create':
                                            // preset_fields.splice(preset_fields.length, 0, 'email','_repeat_email', 'password','_repeat_password')
                                            // Insert at index 3 (after 'email' which is now at index 2 — the 'name' field was inserted ahead of it).
                                            preset_fields.splice(3, 0, 'password');
                                            break;
                                        case 'change_password':
                                            // preset_fields = ['password','_repeat_password'];
                                            preset_fields = ['password'];
                                            break;
                                        default:
                                    }
                                }


                                data_set.available_fields.splice(0, data_set.available_fields.length);
                                data_set.mandatory_fields.splice(0, data_set.mandatory_fields.length);
                                preset_fields.forEach(function (field_name) {
                                    if (!data_set.current_item_field[field_name]) {
                                        console.log('Warning: get_available_fields available_fields item does not exist in current_item_field',field_name,Object.keys(data_set.current_item_field));
                                        return false;
                                    }
                                    data_set.available_fields.push(field_name);
                                    if (optional_fields.indexOf(field_name) === -1) {
                                        data_set.mandatory_fields.push(field_name);
                                    }
                                });
                            },
                            field_change: function (field_name, param2) {
                                let current_object = this;
                                // console.log('allen debug update-user field_change', field_name, param2);
                                switch (field_name) {
                                    case 'company_id':
                                        current_object.get_available_fields();
                                        break;
                                }
                            }
                        };
                        item.component_controllers = ['submit','cancel'];
                        break;
                    case '/admin/update-company':
                        item.component_name = 'form-update-company';
                        // item.component_name = 'form-wrapper';
                        item.module_name = 'company';
                        break;
                    case '/admin/update-tv-show':
                        // item.component_name = 'form-update-tv-show';
                        // item.component_name = 'form-wrapper';
                        item.component_name = 'form-update-item';
                        item.module_name = 'tv_show';
                        break;
                    case '/admin/view-gift-deed':
                    case '/'+base_uri+'/view-gift-deed':
                        item.component_name = 'form-update-item';
                        item.module_name = 'gift_deed';
                        item.component_action = 'view';
                        item.component_controllers = ['cancel'];
                        item.component_methods = {
                            get_available_fields: function (data_set) {
                                let current_object = this;
                                if (typeof data_set === 'undefined') {
                                    data_set = current_object;
                                }
                                let preset_fields = ['id','name','type','gift_amount','delivery_method'];
                                let optional_fields = ['description'];
                                if (data_set.cache_data.available_party_classes) {
                                    preset_fields = preset_fields.concat(Object.keys(data_set.cache_data.available_party_classes));
                                }
                                preset_fields.push('description');
                                preset_fields.push('created_at');
                                preset_fields.push('updated_at');
                                data_set.available_fields.splice(0, data_set.available_fields.length);
                                data_set.mandatory_fields.splice(0, data_set.mandatory_fields.length);
                                preset_fields.forEach(function (field_name) {
                                    if (!data_set.current_item_field[field_name]) {
                                        return false;
                                    }
                                    data_set.available_fields.push(field_name);
                                    if (optional_fields.indexOf(field_name) === -1) {
                                        data_set.mandatory_fields.push(field_name);
                                    }
                                });
                            },
                        };
                        break;
                    case '/admin/view-loan-agreement':
                    case '/'+base_uri+'/view-loan-agreement':
                        item.component_name = 'form-update-item';
                        // item.component_name = 'form-wrapper';
                        item.module_name = 'loan_agreement';
                        item.component_action = 'view';
                        item.component_controllers = ['cancel'];
                        break;
                    case '/admin/view-register-company':
                    case '/'+base_uri+'/view-register-company':
                        item.component_name = 'form-update-item';
                        item.module_name = 'register_company';
                        item.component_action = 'view';
                        item.component_methods = {
                            get_available_fields: function (data_set) {
                                let current_object = this;
                                if (typeof data_set === 'undefined') {
                                    data_set = current_object;
                                }
                                if (!current_object.base) {
                                    current_object.base = current_object;
                                    let parent_object = current_object.$parent;
                                    while (parent_object && parent_object.base) {
                                        current_object.base = parent_object.base;
                                        parent_object = parent_object.$parent;
                                    }
                                }
                                let preset_fields = [];
                                let optional_fields = [];
                                if (current_object.base.component_action) {
                                    switch (current_object.base.component_action) {
                                        case 'view':
                                            preset_fields.push('id');
                                            preset_fields.push('user_id');
                                            preset_fields.push('created_at');
                                            preset_fields.push('name');
                                            preset_fields.push('delivery_method');
                                            preset_fields.push('type');

                                            if (data_set.cache_data.abn) {
                                                preset_fields.push('abn');
                                            }
                                            if (data_set.cache_data.acn) {
                                                preset_fields.push('acn');
                                            }
                                            break;
                                        case 'update':
                                        default:
                                            preset_fields.push('name');
                                            preset_fields.push('delivery_method');
                                            preset_fields.push('abn');
                                            optional_fields.push('abn');
                                            break;
                                    }
                                }

                                preset_fields.push('registration_state');
                                preset_fields.push('register_office_address_id');
                                preset_fields.push('business_address_id');
                                preset_fields.push('description');

                                preset_fields.push('contact');
                                preset_fields.push('officer');
                                preset_fields.push('shareholder');


                                current_object.available_fields.splice(0,current_object.available_fields.length);
                                current_object.mandatory_fields.splice(0,current_object.available_fields.length);
                                preset_fields.forEach(function (field_name) {
                                    current_object.available_fields.push(field_name);
                                    if (optional_fields.indexOf(field_name) === -1 ) {
                                        current_object.mandatory_fields.push(field_name);
                                    }
                                });
                            },

                        };

                        item.component_controllers = ['cancel'];

                        break;
                    case '/admin/view-super':
                    case '/'+base_uri+'/view-super':
                        item.component_name = 'form-update-item';
                        // item.component_name = 'form-wrapper';
                        item.module_name = 'super';
                        item.component_action = 'view';
                        // item.component_action = 'update';

                        // item.confirmation_field = ['id','user_id','created_at','name','type_name','established_at','changed_at','sorted_parties'];
                        // item.preset_fields = {
                        //     edit:['name','type_name','established_at','changed_at','sorted_parties'],
                        //     view:['id','user_id','created_at','name','type_name','established_at','changed_at','sorted_parties']
                        // };

                        // Not used any more in UpdateItem3, keep this to make sure old version form works
                        item.component_methods = {
                            get_available_fields: function (data_set) {
                                let current_object = this;
                                if (typeof data_set === 'undefined') {
                                    data_set = current_object;
                                }
                                if (!current_object.base) {
                                    current_object.base = current_object;
                                    let parent_object = current_object.$parent;
                                    while (parent_object && parent_object.base) {
                                        current_object.base = parent_object.base;
                                        parent_object = parent_object.$parent;
                                    }
                                }
                                // console.log('allen debug get_available_fields order-super',this, current_object === this, current_object.$root === this);
                                let preset_fields = [];
                                if (current_object.base.component_action) {
                                    switch (current_object.base.component_action) {
                                        case 'view':
                                            preset_fields.push('id');
                                            preset_fields.push('user_id');
                                            preset_fields.push('created_at');
                                            preset_fields.push('name');
                                            preset_fields.push('delivery_method');
                                            preset_fields.push('type');
                                            break;
                                        case 'update':
                                        default:
                                            preset_fields.push('type');
                                            preset_fields.push('name');
                                            preset_fields.push('delivery_method');
                                            break;
                                    }
                                }
                                if (data_set.cache_data.type && data_set.cache_data.type === 'SMSFU') {
                                    preset_fields.push('established_at');
                                    preset_fields.push('changed_at');
                                }
                                preset_fields.push('description');

                                // preset_fields.push('sorted_parties');
                                preset_fields.push('contact');
                                preset_fields.push('company_trustee');
                                preset_fields.push('member');
                                preset_fields.push('additional_trustee');

                                data_set.available_fields.splice(0,data_set.available_fields.length);
                                preset_fields.forEach(function (field_name) {
                                    if (!current_object.current_item_field[field_name]) {
                                        console.log('Warning: get_available_fields available_fields item does not exist in current_item_field',field_name,Object.keys(current_object.current_item_field));
                                        return false;
                                    }
                                    data_set.available_fields.push(field_name);
                                });
                            },
                            field_change: function (field_name) {
                                let current_object = this;
                                switch (field_name) {
                                    case 'type':
                                        current_object.get_available_fields();
                                        break;
                                }

                            },
                            // is_editable: function(field_name) {
                            //     let current_object = this;
                            //     if (current_object.base.component_action === 'view') {
                            //         if (current_object.current_item_field[field_name]._edit_mode) {
                            //             return true;
                            //         }
                            //         return false;
                            //     } else {
                            //         if (current_object.$root.panel === 'client') {
                            //             switch (field_name) {
                            //                 case 'id':
                            //                 case 'type':
                            //                     return false;
                            //             }
                            //         }
                            //     }
                            //     return true;
                            // },
                        };

                        item.component_controllers = ['cancel'];
                        break;
                    case '/admin/view-trust':
                    case '/'+base_uri+'/view-trust':
                        item.component_name = 'form-update-item';
                        // item.component_name = 'form-wrapper';
                        item.module_name = 'trust';
                        item.component_action = 'view';


                        item.component_methods = {
                            field_change: function(field) {
// console.log('allen debug field_change beta',field);
                                let current_object = this;
                                let root = current_object.$root;
                                let flag_render = true;
                                switch (field) {
                                    case 'name':
                                        current_object.cache_data.name = root.fix_format(current_object.cache_data.name,[{type:'trust_name'}]);
                                        break;
                                    case 'type':
                                        if (current_object.cache_data && ['DT','HDT','CDT','CVT'].indexOf(current_object.cache_data.type) !== -1) {
                                            current_object.$root.copy_list([
                                                {id:'NC',name:'No Controller'},
                                                {id:'C',name:'with Controller'},
                                            ],current_object.field_available_options.variation);
                                            if (['NC','C'].indexOf(current_object.cache_data.variation) === -1) {
                                                current_object.cache_data.variation = '';
                                            }
                                        }
                                        if (current_object.cache_data && ['HUT','WAPUT','VICPUT'].indexOf(current_object.cache_data.type) !== -1) {
                                            current_object.$root.copy_list([
                                                {id:'TD',name:'Trustee Discretion'},
                                                {id:'PC',name:'Percentage Consent'},
                                            ],current_object.field_available_options.variation);
                                            if (['PC','TD'].indexOf(current_object.cache_data.variation) === -1) {
                                                current_object.cache_data.variation = '';
                                            }
                                        }
                                        if (current_object.cache_data && current_object.cache_data.type === 'UT') {
                                            current_object.$root.copy_list([
                                                {id:'TD',name:'Trustee Discretion'},
                                                {id:'PC',name:'Percentage Consent'},
                                                {id:'AE',name:'Absolute Entitlement'},
                                            ],current_object.field_available_options.variation);
                                            if (['PC','TD','AE'].indexOf(current_object.cache_data.variation) === -1) {
                                                current_object.cache_data.variation = '';
                                            }
                                        }
                                        if (current_object.cache_data && current_object.cache_data.type === 'LTUT') {
                                            current_object.$root.copy_list([
                                                {id:'AE',name:'Absolute Entitlement'},
                                            ],current_object.field_available_options.variation);
                                            if (['AE'].indexOf(current_object.cache_data.variation) === -1) {
                                                current_object.cache_data.variation = '';
                                            }
                                        }
                                        console.log(current_object.current_item_field,root.item_field);
                                        break;
                                    case 'ng':
                                        flag_render = false;
                                        break;
                                }
                                // if (flag_render) {
                                //     current_object.$forceUpdate();
                                // }
                                return true;
                            },
                            get_available_fields: function (data_set) {
                                let current_object = this;
                                if (typeof data_set === 'undefined') {
                                    data_set = current_object;
                                }
                                // console.log('override get_available_fields', current_object);
                                let preset_fields = [];

                                if (data_set.component_action) {
                                    switch (data_set.component_action) {
                                        case 'view':
                                            preset_fields = ['id', 'user_id', 'created_at', 'name', 'delivery_method', 'type', 'variation', 'proper_law'];
                                            break;
                                        case 'update':
                                        default:
                                            preset_fields = ['name', 'delivery_method', 'type', 'variation', 'proper_law'];
                                            break;
                                    }
                                }
                                let optional_fields = [];
                                // let preset_fields = [].concat(current_object.preset_fields[current_object.component_action]||current_object.preset_fields.default||Object.keys(current_object.cache_data));

                                // let available_field_map = {
                                //     DT: ['settlement_sum', 'efp', 'trustee', 'settlor', 'appointor', 'beneficiary', 'controller', 'excluded_class'],
                                //     HDT: ['settlement_sum', 'special_unit_price', 'ng', 'efp', 'trustee', 'settlor', 'appointor', 'beneficiary', 'controller', 'excluded_class', 'special_unit_holder'],
                                //     CDT: ['settlement_sum', 'efp', 'trustee', 'settlor', 'appointor', 'beneficiary_a', 'controller_a', 'beneficiary_b', 'controller_b', 'excluded_class'],
                                //     CVT: ['settlement_sum', 'efp', 'trustee', 'settlor', 'appointor', 'beneficiary', 'beneficiary_cv', 'controller', 'excluded_class'],
                                //     UT: ['unit_price', 'trustee', 'unit_holder'],
                                //     HUT: ['unit_price', 'special_unit_price', 'trustee', 'unit_holder', 'special_unit_holder'],
                                //     LTUT: ['unit_price', 'trustee', 'unit_holder'],
                                //     WAPUT: ['unit_price', 'special_unit_price', 'trustee', 'unit_holder', 'special_unit_holder'],
                                //     VICPUT: ['unit_price', 'special_unit_price', 'trustee', 'unit_holder', 'special_unit_holder'],
                                // };
                                let available_field_map = {
                                    DT: ['settlement_sum', 'efp'],
                                    HDT: ['settlement_sum', 'special_unit_price', 'ng', 'efp'],
                                    CDT: ['settlement_sum', 'efp'],
                                    CVT: ['settlement_sum', 'efp'],
                                    ST: ['settlement_sum','efp','bt','cv','um','variation'],
                                    BT: ['settlement_sum','efp','bt','um','sb'],
                                    BNPT: ['settlement_sum','efp','bt','um','sb'],
                                    UT: ['unit_price'],
                                    HUT: ['unit_price', 'special_unit_price'],
                                    LTUT: ['unit_price'],
                                    WAPUT: ['unit_price'],
                                    VICPUT: ['unit_price'],
                                };

                                // console.log('data_set.cache_data.type', data_set.cache_data.type);
                                if (data_set.cache_data.type && available_field_map[data_set.cache_data.type]) {
                                    preset_fields = preset_fields.concat(available_field_map[data_set.cache_data.type]);
                                    if (['WAPUT','VICPUT'].indexOf(data_set.cache_data.type) > -1 && data_set.cache_data.variation !== 'AE') {
                                        preset_fields.push('special_unit_price');
                                    }
                                }
                                if (data_set.cache_data.variation === 'PC') {
                                    preset_fields.push('consent_trustee_power');
                                }
                                if (data_set.cache_data.type === 'ST') {
                                    if (data_set.cache_data.restrict_appointment_capital) {
                                        preset_fields.push('restrict_appointment_capital');
                                    }
                                    if (data_set.cache_data.variation === 'LD') {
                                        preset_fields.push('income_ld_eq');
                                        preset_fields.push('capital_ld_eq');
                                    }
                                    preset_fields.push('income_eq');
                                    preset_fields.push('capital_eq');
                                }
                                // preset_fields.push('sorted_parties');
                                preset_fields.push('description');

                                // TODO: Not reliable, if it is not loaded by model, appends->available_party_classes is not available
                                if (data_set.cache_data.available_party_classes) {
                                    preset_fields = preset_fields.concat(Object.keys(data_set.cache_data.available_party_classes));
                                } else {
// console.log('allen debug available_party_classes not available',data_set.cache_data,current_object);
                                    preset_fields.push('contact');
                                    preset_fields.push('trustee');
                                    preset_fields.push('settlor');
                                    preset_fields.push('appointor');
                                    preset_fields.push('beneficiary');
                                    preset_fields.push('beneficiary_a');
                                    preset_fields.push('beneficiary_b');
                                    preset_fields.push('beneficiary_cv');
                                    preset_fields.push('beneficiary_grandparent');
                                    preset_fields.push('beneficiary_parent');
                                    preset_fields.push('beneficiary_child');
                                    preset_fields.push('controller');
                                    preset_fields.push('controller_a');
                                    preset_fields.push('controller_b');
                                    preset_fields.push('excluded_class');
                                    preset_fields.push('unit_holder');
                                    preset_fields.push('special_unit_holder');
                                    preset_fields.push('voter');
                                }


                                // data_set.available_fields.splice(0, data_set.available_fields.length);
                                // preset_fields.forEach(function (field_name) {
                                //     if (data_set.component_action === 'view') {
                                //         if (data_set.cache_data.available_party_classes[field_name]) {
                                //             if (data_set.cache_data[field_name].length > 0) {
                                //                 data_set.available_fields.push(field_name);
                                //             }
                                //         } else {
                                //             if (data_set.cache_data[field_name] !== '') {
                                //                 data_set.available_fields.push(field_name);
                                //             }
                                //         }
                                //     } else {
                                //         data_set.available_fields.push(field_name);
                                //     }
                                // };
                                data_set.available_fields.splice(0, data_set.available_fields.length);
                                data_set.mandatory_fields.splice(0, data_set.mandatory_fields.length);
                                preset_fields.forEach(function (field_name) {
                                    if (!data_set.current_item_field[field_name]) {
                                        console.log('Warning: get_available_fields available_fields item does not exist in current_item_field',field_name,Object.keys(data_set.current_item_field));
                                        return false;
                                    }
                                    data_set.available_fields.push(field_name);
                                    if (optional_fields.indexOf(field_name) === -1) {
                                        data_set.mandatory_fields.push(field_name);
                                    }
                                });
                            }
                        };
                        item.component_controllers = ['cancel'];
                        break;

                    case '/'+base_uri+'/my-company-registrations':
                        item.component_name = data_set.panel+'-'+item.name;
                        // item.component_name = 'item-list';
                        // item.request_module = ['bgl_company'];
                        item.list_name = 'register_company';
                        item.list_field = ['name',{name:'user_id',display_name:'Ordered By'},
                            {
                                name:'created_at',
                                display_name:'Order Date',
                                formatted_content:function (date_input) {
                                    return current_object.$dateFormat(date_input, 'DD MMM, YYYY');
                                }
                            }];
                        item.list_field_style = {
                            'lg':{
                                // 'id':'{width: 30%;}',
                                'name':'{width: 30%;}',
                                'user_id':'{width: 20%;}',
                                'created_at':'{width: 10%}'
                            }
                        };
                        item.order_by = 'created_at';
                        item.order_seq = 'desc';
                        item.list_controllers = ['add','view','attach'];
                        break;
                    case '/'+base_uri+'/my-superannuations':
                        item.component_name = data_set.panel+'-'+item.name;
                        item.list_name = 'super';
                        // item.request_module = ['trust','user'];
                        // item.list_field = ['id','type','name','user_id','created_at','updated_at'];
                        item.list_field = ['name','type','user_id',
                            {
                                name:'created_at',
                                display_name:'Order Date',
                                formatted_content:function (date_input) {
                                    return current_object.$dateFormat(date_input, 'DD MMM, YYYY');
                                }
                            }];
                        item.list_field_style = {
                            'lg':{
                                'type':'{width: 15%;}',
                                'user_id':'{width: 20%;}',
                            }
                        };
                        item.list_controllers = ['add','view','attach'];
                        // item.list_controllers = [
                        //     {
                        //         name: 'edit',
                        //         icon_class: 'glyphicon-pencil'
                        //     },
                        //     {
                        //         name: 'view',
                        //         icon_class: 'glyphicon-pencil'
                        //     }
                        //
                        // ];
                        item.list_filter = [
                            {
                                'field':'type',
                                'label':'Filter by Type',
                                'placeholder':'-- Filter by Type --',
                                'option':[
                                    {'value':'DT','display_name':'Discretionary Trust'},
                                    {'value':'HDT','display_name':'Hybrid Discretionary Trust'},
                                    {'value':'CDT','display_name':'Class Discretionary Trust'},
                                    {'value':'CVT','display_name':'Capital Vested Trust'},
                                    {'value':'UT','display_name':'Unit Trust'},
                                    {'value':'HUT','display_name':'Hybrid Unit Trust'},
                                    {'value':'LTUT','display_name':'NSW Land Tax Unit Trust'},
                                    {'value':'WAPUT','display_name':'WA Property Unit Trust'},
                                    {'value':'VICPUT','display_name':'VIC Property Unit Trust'}
                                ]
                            }
                        ];
                        item.order_by = 'created_at';
                        item.order_seq = 'desc';
                        break;
                    case '/'+base_uri+'/my-trusts':
                        // item.component_name = 'item-list';
                        item.component_name = data_set.panel+'-'+item.name;
                        item.list_name = 'trust';
                        item.list_field = ['name','type','user_id',
                            {
                                name:'created_at',
                                display_name:'Order Date',
                                formatted_content:function (date_input) {
                                    return current_object.$dateFormat(date_input, 'DD MMM, YYYY');
                                }
                            }];
                        item.list_field_style = {
                            'lg':{
                                'type':'{width: 20%;}',
                                'user_id':'{width: 20%;}',
                            }
                        };
                        item.list_controllers = ['add',
                            {
                                name: 'view',
                                icon_class: 'glyphicon glyphicon-eye-open'
                            },'attach'
                        ];
                        item.list_filter = [
                            {
                                'field':'type',
                                'label':'Filter by Type',
                                'placeholder':'-- Filter by Type --',
                                'option':[
                                    {'value':'DT','display_name':'Discretionary Trust'},
                                    {'value':'HDT','display_name':'Hybrid Discretionary Trust'},
                                    {'value':'CDT','display_name':'Class Discretionary Trust'},
                                    {'value':'CVT','display_name':'Capital Vested Trust'},
                                    {'value':'UT','display_name':'Unit Trust'},
                                    {'value':'HUT','display_name':'Hybrid Unit Trust'},
                                    {'value':'LTUT','display_name':'NSW Land Tax Unit Trust'},
                                    {'value':'WAPUT','display_name':'WA Property Unit Trust'},
                                    {'value':'VICPUT','display_name':'VIC Property Unit Trust'}
                                ]
                            }
                        ];
                        item.order_by = 'created_at';
                        item.order_seq = 'desc';
                        break;
                    case '/'+base_uri+'/my-loan-agreements':
                        item.component_name = data_set.panel+'-'+item.name;
                        item.list_name = 'loan_agreement';
                        item.list_field = ['address_id',{name:'user_id',display_name:'Ordered By'},
                            {
                                name:'created_at',
                                display_name:'Order Date',
                                formatted_content:function (date_input) {
                                    return current_object.$dateFormat(date_input, 'DD MMM, YYYY');
                                }
                            }];
                        item.list_field_style = {
                            'lg':{
                                // 'id':'{width: 30%;}',
                                'address_id':'{width: 40%;}',
                                'user_id':'{width: 20%;}',
                                'created_at':'{width: 10%}'
                            }
                        };
                        item.order_by = 'created_at';
                        item.order_seq = 'desc';
                        item.list_controllers = ['add','view','attach'];
                        break;
                    case '/'+base_uri+'/my-gift-deeds':
                        item.component_name = data_set.panel+'-'+item.name;
                        item.list_name = 'gift_deed';
                        item.list_field = ['name',{name:'user_id',display_name:'Ordered By'},
                            {
                                name:'created_at',
                                display_name:'Order Date',
                                formatted_content:function (date_input) {
                                    return current_object.$dateFormat(date_input, 'DD MMM, YYYY');
                                }
                            }];
                        item.list_field_style = {
                            'lg':{
                                // 'id':'{width: 30%;}',
                                'name':'{width: 40%;}',
                                'user_id':'{width: 20%;}',
                                'created_at':'{width: 10%}'
                            }
                        };
                        item.order_by = 'created_at';
                        item.order_seq = 'desc';
                        item.list_controllers = ['add','view','attach'];
                        break;
                    case '/'+base_uri+'/my-details':
                        item.component_name = 'client-my-details';
                        item.list_name = 'user';
                        item.list_field = ['id',{name:'formatted_name',display_name:'Name'},'email'];
                        // apply to ItemList only
                        item.list_field_style = {
                            'lg':{
                                'id':'{width: 5%;}',
                                'formatted_name':'{width: 20%;}',
                                'email':'{width: 35%;}',
                                'company_admin':'{width: 10%;}'
                            }
                        };
                        item.list_controllers = ['add','edit','delete'];
                        item.order_by = 'id';
                        item.order_seq = 'asc';

                        item.module_name = 'user';
                        item.component_action = 'update';
                        item.component_methods = {
                            get_available_fields: function (data_set) {
                                let current_object = this;
                                if (typeof data_set === 'undefined') {
                                    data_set = current_object;
                                }
                                // console.log('override get_available_fields', current_object);
                                let preset_fields = ['title', 'first_name', 'middle_name', 'last_name', 'membership'];
                                let optional_fields = ['middle_name', 'membership'];
                                if (data_set.component_action) {
                                    switch (data_set.component_action) {
                                        case 'create':
                                            // preset_fields.splice(preset_fields.length, 0, 'email','_repeat_email', 'password','_repeat_password')
                                            preset_fields.splice(preset_fields.length, 0, 'email','password');
                                            break;
                                        case 'change_password':
                                            // preset_fields = ['password','_repeat_password'];
                                            preset_fields = ['password'];
                                            break;
                                        default:
                                    }
                                }

                                data_set.available_fields.splice(0, data_set.available_fields.length);
                                data_set.mandatory_fields.splice(0, data_set.mandatory_fields.length);
                                preset_fields.forEach(function (field_name) {
                                    if (!data_set.current_item_field[field_name]) {
                                        console.log('Warning: get_available_fields available_fields item does not exist in current_item_field',field_name,Object.keys(data_set.current_item_field));
                                        return false;
                                    }
                                    data_set.available_fields.push(field_name);
                                    if (optional_fields.indexOf(field_name) === -1) {
                                        data_set.mandatory_fields.push(field_name);
                                    }
                                });
                            }
                        };
                        item.component_controllers = ['submit','cancel'];
                        break;
                    case '/client/home':
                        item.component_name = 'client-home';
                        break;
                    case '/'+base_uri+'/home':
                        item.component_name = 'client-content';
                        break;
                    case '/'+base_uri+'/404':
                        item.component_name = data_set.panel+'-not-found';
                        break;
                    case '/'+base_uri+'/aml':
                    case '/client/aml':
                        item.component_name = 'client-aml';
                        break;
                    default:
                        // if (data_set.panel === 'admin') {
                        //     let prefix_regex = new RegExp('^/'+root.panel+'/([^-]+)-([^/]+)','i');
                        //
                        //     if (prefix_regex.test(location.pathname)) {
                        //         let component_action = location.pathname.replace(prefix_regex, '$1');
                        //         if (['update','view'].indexOf(component_action) !== -1) {
                        //             item.component_action = component_action;
                        //             let module_name = location.pathname.replace(prefix_regex, '$2');
                        //             module_name = module_name.replace('-', '_');
                        //             if (['gift_deed','custom_theme','custom_page'].indexOf(module_name) !== -1) {
                        //                 item.module_name = module_name;
                        //             }
                        //             item.component_name = 'form-update-item';
                        //         }
                        //
                        //     }
                        // }
                        if (!item.component_name) {
                            item.component_name = data_set.panel+'-'+item.name;
                            // Vue 3: globally registered components live on app._context.components
                            if (!app._context.components[item.component_name]) {
                                item.component_name = data_set.panel+'-default';
                            }
                        }

                    // item.component_name = 'item-list';
                }
            });

console.log('[DEBUG] checkpoint B - before menu check', !!data_set.item_map.page, !!data_set.item_map.uri_pathname);
            if (data_set.item_list['menu'] && data_set.item_list['menu'].length > 0 && !data_set.current_id['menu']) {
                data_set.current_id['menu'] = data_set.item_list['menu'][0]['id'];
                if (data_set.item_map.page[data_set.item_list['menu'][0]['page_id']]) {
                    data_set.item_map['uri_pathname']['/'+base_uri] = data_set.item_map.page[data_set.item_list['menu'][0]['page_id']];
                }
            }

            let current_uri_path = location.pathname;
            let path_parts = current_uri_path.split('/');

            // AML sub-routes: treat /client/aml/* and /aml/* as /client/aml or /aml
            let effective_pathname = location.pathname;
            if (effective_pathname.startsWith('/client/aml/')) {
                effective_pathname = '/client/aml';
            } else if (effective_pathname.match(/^\/aml\/.+/)) {
                effective_pathname = '/' + base_uri + '/aml';
            }

            if (data_set.item_map['uri_pathname'][effective_pathname]) {
                data_set.current_id.page = data_set.item_map['uri_pathname'][effective_pathname].id;
                if (data_set.item_map['uri_pathname'][effective_pathname].menu_list && data_set.item_map['uri_pathname'][effective_pathname].menu_list.length >= 1) {
                    data_set.current_id.menu = data_set.item_map['uri_pathname'][effective_pathname].menu_list[0];
                } else {
                    data_set.current_id.menu = 0;
                }
            } else {
                data_set.current_id.page = data_set.item_map['uri_pathname']['/'+base_uri+'/404'].id;
            }
        }

        if (!data_set.item_map['payment_method']) {
            data_set.item_map['payment_method'] = {};
        }
        if (data_set.item_list['braintree_credit_card']) {
            data_set.item_list['braintree_credit_card'].forEach(function (payment_method) {
                data_set.item_map['payment_method'][payment_method.token] = payment_method;
            });
        }
        /*Object.keys(data_set.item_list).forEach(function (module_name) {
            if(/^left_menu_/.test(module_name)) {
                let page_title = 'Unknown';
                let left_base_pathname = module_name.replace(/^left_menu_/, '');
                left_base_pathname = '/'+data_set.panel+'/'+left_base_pathname.replaceAll('_','-');
                if (data_set.item_map['uri_pathname'][left_base_pathname]) {
                    page_title = data_set.item_map['uri_pathname'][left_base_pathname].display_name;
                    data_set.item_map['uri_pathname'][left_base_pathname].component_name = 'client-content-with-left-menu';
                    data_set.item_map['uri_pathname'][left_base_pathname].left_menu_name = module_name;
                    data_set.item_map['uri_pathname'][left_base_pathname].page_title = page_title;
                }
                data_set.item_list[module_name].forEach(function (left_menu_item) {
                    if (left_menu_item.page_id && data_set.item_map.page[left_menu_item.page_id]) {
                        data_set.item_map.page[left_menu_item.page_id].component_name = 'client-content-with-left-menu';
                        data_set.item_map.page[left_menu_item.page_id].left_menu_name = module_name;
                        data_set.item_map.page[left_menu_item.page_id].page_title = page_title;
                    }
                    if (left_menu_item._ref_list_children) {
                        left_menu_item._ref_list_children.forEach(function (left_menu_item) {
                            if (left_menu_item.page_id && data_set.item_map.page[left_menu_item.page_id]) {
                                data_set.item_map.page[left_menu_item.page_id].component_name = 'client-content-with-left-menu';
                                data_set.item_map.page[left_menu_item.page_id].left_menu_name = module_name;
                                data_set.item_map.page[left_menu_item.page_id].page_title = page_title;
                            }
                        });
                    }
                });
            }
        });*/
console.log('[DEBUG] checkpoint C - before left_menu');
        if(data_set.item_list['left_menu']) {
            let left_menu_stack = current_object.copy_list(data_set.item_list['left_menu']);
            let left_menu_item;
            while (left_menu_stack.length) {
                left_menu_item = left_menu_stack.shift();

                left_menu_item._ref_root = left_menu_item;
                while (left_menu_item._ref_root._ref_parent) {
                    left_menu_item._ref_root = left_menu_item._ref_root._ref_parent;
                }

                if (left_menu_item._ref_page) {
                    // Only force ContentWithLeftMenu when no page-specific Vue
                    // component is registered. Pages like About MGS Private
                    // (client-about-mgs-private) have their own bespoke layout
                    // that reproduces the sidebar internally — they shouldn't
                    // get clobbered by the generic shell. Always preserve
                    // _ref_left_menu so the bespoke component can read the
                    // sidebar data if it needs to.
                    let specific_name = data_set.panel + '-' + left_menu_item._ref_page.name;
                    if (!app._context.components[specific_name]) {
                        left_menu_item._ref_page.component_name = 'client-content-with-left-menu';
                    }
                    left_menu_item._ref_page._ref_left_menu = left_menu_item._ref_root;
                }
                if (left_menu_item._ref_list_children) {
                    current_object.copy_list(left_menu_item._ref_list_children,left_menu_stack,true);
                }
            }
            /*data_set.item_list['left_menu'].forEach(function (left_menu_item) {
                let left_menu_root = left_menu_item;
                let left_menu_root_page = {display_name:'Unknown'};
                if (left_menu_item.page_id && data_set.item_map.page[left_menu_item.page_id]) {
                    data_set.item_map.page[left_menu_item.page_id].component_name = 'client-content-with-left-menu';
                    left_menu_root_page = data_set.item_map.page[left_menu_item.page_id];
                    data_set.item_map.page[left_menu_item.page_id].root_page = left_menu_root_page;
                    data_set.item_map.page[left_menu_item.page_id].page_title = left_menu_root_page.display_name;
                }
                if (left_menu_item._ref_list_children) {
                    left_menu_item._ref_list_children.forEach(function (left_menu_item) {
                        if (left_menu_item.page_id && data_set.item_map.page[left_menu_item.page_id]) {
                            data_set.item_map.page[left_menu_item.page_id].component_name = 'client-content-with-left-menu';
                            data_set.item_map.page[left_menu_item.page_id].root_page = left_menu_root_page;
                            data_set.item_map.page[left_menu_item.page_id].page_title = left_menu_root_page.display_name;
                        }
                        if (left_menu_item._ref_list_children) {
                            left_menu_item._ref_list_children.forEach(function (left_menu_item) {
                                if (left_menu_item.page_id && data_set.item_map.page[left_menu_item.page_id]) {
                                    data_set.item_map.page[left_menu_item.page_id].component_name = 'client-content-with-left-menu';
                                    data_set.item_map.page[left_menu_item.page_id].root_page = left_menu_root_page;
                                    data_set.item_map.page[left_menu_item.page_id].page_title = left_menu_root_page.display_name;
                                }
                            });
                        }
                    });
                }
            });*/
        }
        data_set.page_component_id = 0;

        // console.log('data_set final result',data_set);
        return data_set;
      } catch(e) {
        console.error('[DATA_ERROR]', e.message, e.stack);
        return { item_map: { page: {}, uri_pathname: {} }, item_list: {}, current_id: { page: 0 }, flag_loading: 0, message_option: { message_type: 'error', message: 'Init error: ' + e.message }, panel: 'client', base_uri: 'client', page_component_id: 0 };
      }
    },
    computed: {
        uri_query: function () {
            let current_object = this;
            let regex_query = new RegExp('([^?]*)[?]([^#]*)');
            let regex_results = regex_query.exec(location.href);
            let result = {};
            if (regex_results && regex_results[2]) {
                let search = regex_results[2];
                result = JSON.parse('{"' + search.replace(/&/g, '","').replace(/=/g,'":"') + '"}', function(key, value) { return key===""?value:decodeURIComponent(value) })
            }
            return result;
        }
    },
    methods: {
        display_message: function(message, type, message_option) {
            if (typeof message_option === 'undefined') {
                message_option = this.message_option;
            }
            message_option.message_type = type;
            message_option.message = message;
        },
        // ajax loading data from database (load one or multiple tables)
        load_module: function(option) {
            // load multiple modules on one ajax call, reduce unnecessary HTTP requests
            let current_object = this;
            if (!option || !option.request_module || typeof option.request_module !== 'object') {
                console.log('Error, admin load_module option error',option);
                return;
            }
            // expected request_module in object, e.g. {'user':{'reload_interval':1,'limit':100},'company':{}}
            let request_module = {};
            if (Array.isArray(option.request_module)) {
                option.request_module.forEach(function (module_name) {
                    request_module[module_name] = {};
                });
            } else {
                request_module = Object.assign(request_module,option.request_module);
            }
            let current_time = new Date();
            Object.keys(request_module).forEach(function (module_name) {
                if (typeof request_module[module_name]['reload_interval'] === 'undefined') {
                    request_module[module_name]['reload_interval'] = 7200000;      // milliseconds, 3600000 = 1 hour; Any module loaded for more than {reload_interval} time, should be reloaded
                }
                let reload_interval = request_module[module_name]['reload_interval'];

                if (current_object.loaded[module_name] && current_time - current_object.loaded[module_name] <= reload_interval ) {
                    delete request_module[module_name];
                }
            });
            let call_back_data = {status:'OK'};
            if (Object.keys(request_module).length === 0) {
                // everything already loaded
                if (typeof option.call_back === 'function') {
                    option.call_back(call_back_data);
                }
                return;
            }
            Object.keys(request_module).forEach(function (module_name) {
                if (!current_object.item_field[module_name]) {
                    request_module[module_name]['request_field'] = true;
                }
            });
            // let message = [];
            // if (option.message) {
            //     message = option.message;
            // } else {
            //     message = current_object.message;
            // }
            current_object.flag_loading++;
            axios.post('/'+current_object.panel+'/get_module',{
                render_type: 'json',
                request_module: request_module
            }).then(function (response) {
                current_object.flag_loading--;
                if (response.data && response.data.status === 'OK') {
                    if (response.data.item_field) {
                        current_object.set_item_field(response.data.item_field);
                        // TODO: put this part in a general function
                        // if (!current_object.item_field) {
                        //     current_object.item_field = {};
                        // }
                        // Object.keys(response.data.item_field).forEach(function (module_name) {
                        //     if (!current_object.item_field[module_name]) {
                        //         current_object.item_field[module_name] = {};
                        //     }
                        //     current_object.set_field(response.data.item_field[module_name],current_object.item_field[module_name]);
                        // };
                    }
                    current_object.set_item_list(response.data.item_list);
                    Object.keys(response.data.item_list).forEach(function (module_name) {
                        // let result = current_object.set_module_map(response.data.item_list[module_name],module_name);
                        //
                        // call_back_data[module_name] = result;
                        current_object.loaded[module_name] = new Date();
                    });
                    if (typeof option.call_back === 'function') {
                        option.call_back(response.data);
                    }
                } else {
                    if (response.data && response.data.message) {
                        current_object.display_message(response.data.message,'error');
                    } else {
                        current_object.display_message('Page loading failed, please try to refresh after few minutes.','error');
                        console.log('Page load unexpected error',response);
                    }
                }
            }).catch(function (error) {
                current_object.flag_loading--;
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    console.log(error.response.data);
                    console.log(error.response.status);
                    console.log(error.response.headers);

                    if (error.response.status === 500) {
                        current_object.message_option.message_type = 'error';
                        current_object.message_option.message = 'An error occurred. You might have left the page open too long. Please refresh the page and try again. If this problem continues to occur, please contact us.';

                        // If it is server level error, it can probably solved by refreshing page (e.g. IIS temporarily not responding; browser was left untouched too long, csrf failed...)
                        setTimeout(function () {
                            location.reload();
                        },3000);
                    }

                } else if (error.request) {
                    // The request was made but no response was received
                    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                    // http.ClientRequest in node.js
                    console.log(error.request);
                } else {
                    // Something happened in setting up the request that triggered an Error
                    console.log('Error', error.message);
                }
                console.log('get_module error',request_module,error);
                if (typeof option.call_back === 'function') {
                    option.call_back(null);
                }
                // message.push('get_module failed, HTTP request error');
            });
        },

        // ajax loading item data from database (load one or multiple rows from given table and related rows from other tables, e.g. load user A, B and all their comments)
        load_detail: function(option) {
            // Input required: module - module name in string, field - optional, get_item by field, default as "id", key - value of given field
            // Alternatively, provide option.item_list as item object list; or, provide option.id = xxx, which is equivalent to {field = 'id', key = xxx}
            let current_object = this;
            let post_data = {render_type: 'json', item_format: 'model'};
            if (!option || !option.module || typeof option.module !== 'string') {
                console.log('Error, admin load_detail option error',option);
                return;
            }
            post_data.module = option.module;
            if (!current_object.item_field[option.module]) {
                post_data.request_field = true;
            }
            if (!option.key) {
                if (option.item_list) {
                    post_data.field = 'id';
                    post_data.key = [];
                    option.item_list.forEach(function (item) {
                        // if (item.detail_ready) {
                        //     return true;
                        // }
                        post_data.key.push(item.id);
                    });
                } else {
                    if (option.id) {
                        post_data.field = 'id';
                        post_data.key = option.id;
                    }
                }
            } else {
                post_data.key = option.key;
                if (option.field) {
                    post_data.field = option.field;
                }
            }
            let call_back_data = {status:'OK',request:option};
            if (typeof post_data.key !== 'object' || !post_data.key.length) {
                if (typeof option.call_back === 'function') {
                    option.call_back(call_back_data);
                }
                return;
            }
            current_object.flag_loading++;
            axios.post('/'+current_object.panel+'/get_item',post_data).then(function (response) {
                // console.log('allen debug load_detail callback',response);
                current_object.flag_loading--;
                if (response.data && response.data.status === 'OK') {
                    current_object.set_item_list(response.data.item_list);
                    if (typeof option.call_back === 'function') {
                        option.call_back(response.data);
                    }
                } else {
                    if (response.data && response.data.message) {
                        current_object.display_message(response.data.message,'error');
                    } else {
                        current_object.display_message('Page loading failed, please try to refresh after few minutes.','error');
                        console.log('Page load unexpected error',response);
                    }
                }
            }).catch(function (error) {
                current_object.flag_loading--;
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    console.log(error.response.data);
                    console.log(error.response.status);
                    console.log(error.response.headers);

                    if (error.response.status >= 500) {
                        // If it is server level error, it can probably solved by refreshing page (e.g. IIS temporarily not responding; browser was left untouched too long, csrf failed...)
                        setTimeout(function () {
                            location.reload();
                        },3000);
                    }
                } else if (error.request) {
                    // The request was made but no response was received
                    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                    // http.ClientRequest in node.js
                    console.log(error.request);
                } else {
                    // Something happened in setting up the request that triggered an Error
                    console.log('Error', error.message);
                }
                console.log('load_detail error',post_data,error);
                if (typeof option.call_back === 'function') {
                    call_back_data.status = 'Error';
                    if (error.message) {
                        call_back_data.message = error.message;
                    }
                    option.call_back(call_back_data);
                }
                // message.push('get_module failed, HTTP request error');
            });
        },

        // General Panel Functions
        // Reading data rows of data_attachment table, get all available fields (additional fields) for modules
        /*set_data_attachment: function(data_attachment, data_set) {
console.log('allen debug set_data_attachment',data_attachment, data_set);
            let current_object = this;
            if (typeof data_set === 'undefined') {
                data_set = current_object;
            }

            if (!data_set.item_map['attached_field']) {
                data_set.item_map['attached_field'] = {};
            }
            data_attachment.forEach(function (data_row) {
                if (data_row.content && typeof data_row.content === 'string') {
                    data_row.content = JSON.parse(data_row.content);
                }
                if (data_row.reference_type && data_set.item_map[data_row.reference_type]) {
                    if (data_row.reference_id && data_set.item_map[data_row.reference_type][data_row.reference_id]) {
                        if (data_row.deleted) {
                            delete data_set.item_map[data_row.reference_type][data_row.reference_id]['data_attachment_id'];
                            delete data_set.item_map[data_row.reference_type][data_row.reference_id]['data_attachment'];

                            Object.keys(data_row.content).forEach(function (additional_data_key) {
console.log('additional_data_key',data_row.reference_type,additional_data_key,current_object.item_field,current_object.item_field[data_row.reference_type]);
                                if (current_object.item_field[data_row.reference_type][additional_data_key]) {
                                    if (typeof current_object.item_field[data_row.reference_type][additional_data_key]['default_value'] !== 'undefined') {
                                        data_set.item_map[data_row.reference_type][data_row.reference_id][additional_data_key] = current_object.item_field[data_row.reference_type][additional_data_key]['default_value'];
                                    } else {
                                        delete data_set.item_map[data_row.reference_type][data_row.reference_id][additional_data_key];
                                    }
                                }
                            });
                        } else {
                            data_set.item_map[data_row.reference_type][data_row.reference_id]['data_attachment_id'] = data_row.id;
                            data_set.item_map[data_row.reference_type][data_row.reference_id]['data_attachment'] = data_row;

                            if (!data_set.item_map['attached_field'][data_row.reference_type]) {
                                data_set.item_map['attached_field'][data_row.reference_type] = [];
                            }

                            Object.keys(data_row.content).forEach(function (additional_data_key) {
                                data_set.item_map[data_row.reference_type][data_row.reference_id][additional_data_key] = data_row.content[additional_data_key];

                                if (data_set.item_map['attached_field'][data_row.reference_type].indexOf(additional_data_key) === -1) {
                                    data_set.item_map['attached_field'][data_row.reference_type].push(additional_data_key);
                                }
                            })
                        }
                    }
                }
            });
            return data_set.item_map['attached_field'];
        },*/

        set_item_list: function(item_list, data_set) {
            let current_object = this;
            if (typeof data_set === 'undefined') {
                data_set = current_object;
            }

            // set up data_set.item_list, data_set.item_map from given item_list
            current_object.set_item_list_map(item_list, data_set);

            // set up data_set.item_field from given item_list
            current_object.set_item_list_field(item_list, data_set);

            // set up item detail, e.g. referenced object from FK, additional columns for list sorting purpose, default value of attached fields and etc.
            current_object.set_item_list_relationship(item_list, data_set);
        },

        // Reading data rows and put them into map object. Note: returned items are not the same as input ones, new ones match the map items
        set_map: function(items, map, cache) {
            let current_object = this;
            let result = [];

            items.forEach(function (item) {
                if (map[item.id]) {
                    // item = Object.assign(map[item.id],item);
                    Object.keys(item).forEach(function (field_name) {
                        if (/^_/.test(field_name)) {
                            // fields leading by _ are fields for special functions
                            return false;
                        }
                        map[item.id][field_name] = JSON.parse(JSON.stringify(item[field_name]));
                    });
                    item = map[item.id];
                    if (item.deleted) {
                        delete map[item.id];
                    }
                } else {
                    if (!item.deleted) {
                        map[item.id] = {};
                        // item = Object.assign(map[item.id],item);
                        Object.keys(item).forEach(function (field_name) {
                            if (/^_/.test(field_name)) {
                                // fields leading by _ are fields for special functions
                                return false;
                            }
                            // Vue 3 perf: skip redundant JSON clone on bulk initial load.
                            // Source array goes out of scope after set_map; no caller holds a ref.
                            // Form-isolation is enforced by copy_item / import_item which clone for cache_data.
                            map[item.id][field_name] = item[field_name];
                        });
                        item = map[item.id];
                    }
                }
                if (cache) {
                    if (!Array.isArray(cache)) {
console.log('allen debug set_map cache is not array',cache);
                    }
                    if (item.deleted) {
                        let current_index = cache.indexOf(item);
                        if (current_index !== -1) {
                            cache.splice(current_index,1);
                        }
                    } else {
                        let current_index = cache.indexOf(item);
                        if (current_index === -1) {
                            cache.unshift(item);
                        }
                    }
                }

                result.push(item);
            });
            items.splice(0,items.length);
            result.forEach(function (item) {
                items.push(item);
            });
            return items;
        },

        // set_map by module name, e.g. module_name = 'trust' and data_set = $root (default), items are considered trust rows and are set into global map in $root
        set_module_map: function(items, module_name, data_set) {
            // Alt for set_map, reference root map and cache by module_name, can only be used after initiation
            let current_object = this;
            if (typeof data_set === 'undefined') {
                data_set = current_object;
            }
            if (!data_set.item_map[module_name]) {
                data_set.item_map[module_name] = {};
            }
            if (!data_set.item_list[module_name]) {
                data_set.item_list[module_name] = [];
            }

            items = current_object.set_map(items, data_set.item_map[module_name], data_set.item_list[module_name]);
            // Set up special item_map
            switch (module_name) {
                case 'bgl_contact':
                    data_set.item_list[module_name].forEach(function (item) {
                        if (typeof item.representative_ids === 'string') {
                            item.representative_ids = JSON.parse(item.representative_ids);
                        }
                    });
                    break;
                case 'braintree_credit_card':
                    if (!data_set.item_map['payment_method']) {
                        data_set.item_map['payment_method'] = {};
                    }
                    items.forEach(function (item) {
                        if (item.deleted) {
                            if (data_set.item_map['payment_method'][item.token]) {
                                delete data_set.item_map['payment_method'][item.token];
                            }
                        } else {
                            data_set.item_map['payment_method'][item.token] = item;
                        }
                    });

                    break;
                // case 'custom_page_video':
                //     let menu_item = {
                //         id: '_video_root',
                //         name: 'video',
                //         type: 'top_menu_client',
                //         display_name: 'Video',
                //         display_order: 999
                //     };
                //     data_set.item_map['menu'][menu_item.id] = menu_item;
                //     data_set.item_list['top_menu_client'].push(menu_item);
                //     data_set.root_menu_list.push(menu_item.id);
                //     items.forEach(function(item){
                //         let menu_item = {
                //             id: '_video_'+item.id,
                //             name: item.name,
                //             type: 'top_menu_client',
                //             display_name: item.display_name,
                //             display_order: item.display_order,
                //             page_id: '_video_'+item.id,
                //             parent_id: 0
                //         };
                //         let page_item = {
                //             id: '_video_'+item.id,
                //             name: item.name,
                //             display_name: item.display_name,
                //             content: item.content
                //         };
                //         data_set.item_map['menu'][menu_item.id] = menu_item;
                //         data_set.item_list['left_menu'].push(menu_item);
                //         data_set.item_map['page'][page_item.id] = page_item;
                //         data_set.item_list['page'].push(page_item);
                //         if (item.parent_id === 0) {
                //             menu_item = Object.assign({parent_id:'_video_root'},menu_item)
                //             data_set.item_list['top_menu_client'].push(menu_item);
                //         } else {
                //             menu_item.parent_id = '_video_'+item.parent_id;
                //         }
                //
                //     };
                //     break;
                case 'data_attachment':
                    if (!data_set.item_field) {
                        data_set.item_field = {};
                    }
                    items.forEach(function (data_row) {
                        if (data_row.content && typeof data_row.content === 'string') {
                            data_row.content = JSON.parse(data_row.content);
                        }
                        if (!data_set.item_field[data_row.reference_type]) {
                            data_set.item_field[data_row.reference_type] = {};
                        }

                        if (!data_set.item_field[data_row.reference_type]['_list']) {
                            data_set.item_field[data_row.reference_type]['_list'] = {};
                        }
                        if (!data_set.item_field[data_row.reference_type]['_list']['attached']) {
                            data_set.item_field[data_row.reference_type]['_list']['attached'] = [];
                        }

                        Object.keys(data_row.content).forEach(function (additional_data_key) {
                            if (data_set.item_field[data_row.reference_type]['_list']['attached'].indexOf(additional_data_key) === -1) {
                                data_set.item_field[data_row.reference_type]['_list']['attached'].push(additional_data_key);
                            }
                        });
                        if (!data_set.item_map[data_row.reference_type+'_data_attached']) {
                            data_set.item_map[data_row.reference_type+'_data_attached'] = {};
                        }
                        if (!data_set.item_map[data_row.reference_type+'_data_attached'][data_row.reference_id]) {
                            data_set.item_map[data_row.reference_type+'_data_attached'][data_row.reference_id] = {data_attachment_id:data_row.id};
                        }
                        Object.keys(data_set.item_map[data_row.reference_type+'_data_attached'][data_row.reference_id]).forEach(function (additional_key) {
                            if (typeof data_row.content[additional_key] === 'undefined') {
                                delete data_set.item_map[data_row.reference_type+'_data_attached'][data_row.reference_id][additional_key];
                            }
                        });
                        data_set.item_map[data_row.reference_type+'_data_attached'][data_row.reference_id] = Object.assign(data_set.item_map[data_row.reference_type+'_data_attached'][data_row.reference_id],{data_attachment_id:data_row.id});
                        data_set.item_map[data_row.reference_type+'_data_attached'][data_row.reference_id] = Object.assign(data_set.item_map[data_row.reference_type+'_data_attached'][data_row.reference_id],data_row.content);
                    });
                    break;
                case 'document':
                    // data_set.item_list[module_name] = _.orderBy(data_set.item_list[module_name], 'id', 'asc');
                    items.forEach(function(item){
                        if (!data_set.item_map[item.reference_type+'_document']) {
                            data_set.item_map[item.reference_type+'_document'] = {};
                        }
                        if (!data_set.item_map[item.reference_type+'_document'][item.reference_id]) {
                            data_set.item_map[item.reference_type+'_document'][item.reference_id] = {};
                        }
                        if (item.deleted) {
                            delete data_set.item_map[item.reference_type+'_document'][item.reference_id][item.id];
                        } else {
                            data_set.item_map[item.reference_type+'_document'][item.reference_id][item.id] = item;
                        }
                        // if (!data_set.item_map[document.reference_type]) {
                        //     data_set.item_map[document.reference_type] = {};
                        // }
                        // if (!data_set.item_map[document.reference_type][document.reference_id]) {
                        //     data_set.item_map[document.reference_type][document.reference_id] = {};
                        // }
                        // data_set.item_map[document.reference_type][document.reference_id]['_list']
                    });
                    break;
                case 'menu':
                    let menu_name_list = [];
                    data_set.item_list[module_name].forEach(function (item) {
                        if (!data_set.item_list[item.type]) {
                            data_set.item_list[item.type] = [];
                            menu_name_list.push(item.type);
                        }
                        if (item.parent_id === 0) {
                            data_set.item_list[item.type].push(item);
                        }
                    });
                    menu_name_list.forEach(function (item_type) {
                        data_set.item_list[item_type] = _.orderBy(data_set.item_list[item_type], 'display_order', 'asc');
                    });
                    break;
                case 'package':
                    data_set.item_list[module_name] = _.orderBy(data_set.item_list[module_name], 'id', 'asc');
                    data_set.item_list[module_name].forEach(function(order_package){
                        if (!order_package.type) {
                            return;
                        }
                        if (!data_set.item_map[order_package.module_name+'_type']) {
                            data_set.item_map[order_package.module_name+'_type'] = {};
                        }
                        data_set.item_map[order_package.module_name+'_type'][order_package.type] = order_package;
                    });
                    break;
                case 'page':
                    // TODO: merge uri_pathname map here and move additional page param definitions to db
                    break;
                case 'tv_show':
                    data_set.item_list[module_name] = _.orderBy(data_set.item_list[module_name], 'display_order', 'asc');
                    break;
            }
            if (module_name.match(/_party$/)) {
                items.forEach(function (item) {
                    if (item.type === 'Trust' && item.children) {
                        item.trustee = [];
                        item.children.forEach(function (sub_item) {
                            item.trustee.push(sub_item.id);
                        });
                    }
                });
            }
            return items;
        },

        // set_map by item_list, item_list contains multiple module_name => data_rows pair,
        // use this function with load_module and similar functions, success axios result should contain item_list:{module1_name:[row1,row2...],module2_name:[row1,row2...],module3_name:[row1,row2...]}
        set_item_list_map: function(item_list, data_set) {
            let current_object = this;
            if (typeof data_set === 'undefined') {
                data_set = current_object;
            }
            Object.keys(item_list).forEach(function (module_name) {
                current_object.set_module_map(item_list[module_name], module_name, data_set);
            });
            return item_list;
        },

        // set_field function has no return value, fields (name strings) are set into field_map (object),
        // field_map[field_name] might already have some attributes defined, this function help complete the rest default value
        set_field: function(fields, field_map) {
            let current_object = this;
            switch (typeof fields) {
                case 'string':
                    fields = fields.split(',');
                case 'object':
                    if (Array.isArray(fields)) {
                        let fields_object = {};
                        fields.forEach(function (field_name) {
                            if (typeof field_name === 'string') {
                                fields_object[field_name] = {name:field_name};
                            }
                            if (typeof field_name === 'object' && field_name['name']) {
                                fields_object[field_name['name']] = Object.assign({},field_name);
                            }
                        });
                        fields = fields_object;
                    }
                    Object.keys(fields).forEach(function (field_name) {
if (field_name === 'beneficiary_parent') {
    console.log('test point 1',field_name,fields[field_name],field_map[field_name]);

}
                        if (/^_/.test(field_name)) {
                            // fields leading by _ are fields for special functions
                            return false;
                        }
                        // if (['sorted_parties'].indexOf(field_name) >= 0) {
                        //     return false;
                        // }
                        if (!field_map[field_name]) {
                            field_map[field_name] = {};
                        }
                        if (field_map[field_name] !== fields[field_name]) {
                            field_map[field_name] = Object.assign(field_map[field_name],fields[field_name]);
                        }
                        if (!field_map[field_name].name) {
                            field_map[field_name].name = field_name;
                        }

                        if (field_map[field_name].field_type) {
                            switch (field_map[field_name].field_type) {
                                case 'date':
                                case 'datetime-local':
                                    if(!field_map[field_name].formatted_content) {
                                        field_map[field_name].formatted_content = function (current_value) {
                                            if (current_value) {
                                                return current_object.$dateFormat(current_value, 'DD MMM, YYYY');
                                            }
                                        }
                                    }
                                    break;
                                case 'checkbox':
                                    if(!field_map[field_name].formatted_content) {
                                        field_map[field_name].formatted_content = {
                                            '0': 'No',
                                            '1': 'Yes'
                                        }
                                    }
                                    break;

                            }
                        }

                        switch (field_name) {
                            case 'id':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'ID';
                                if (!field_map[field_name].field_type) {
                                    field_map[field_name].field_type = 'text';
                                }
                                if(!field_map[field_name].order_seq_default) field_map[field_name].order_seq_default = 'desc';
                                if(!field_map[field_name].keywords_search) field_map[field_name].keywords_search = 'exact';
                                if(!field_map[field_name].keywords_search_min_char) field_map[field_name].keywords_search_min_char = 1;
                                if (typeof field_map[field_name].default_value === 'undefined') {
                                    field_map[field_name].default_value = 0;
                                }
                                break;
                            case 'created_at':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Created Date';
                                if (!field_map[field_name].field_type) {
                                    field_map[field_name].field_type = 'date';
                                }
                                if(!field_map[field_name].order_type) field_map[field_name].order_type = 'date';
                                if(!field_map[field_name].order_seq_default) field_map[field_name].order_seq_default = 'desc';
                                break;
                            case 'changed_at':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Changed Date';
                                if (!field_map[field_name].field_type) {
                                    field_map[field_name].field_type = 'date';
                                }
                                if(!field_map[field_name].order_type) field_map[field_name].order_type = 'date';
                                if(!field_map[field_name].formatted_content) {
                                    field_map[field_name].formatted_content = function (current_value) {
                                        if (current_value) {
                                            return current_object.$dateFormat(current_value, 'DD MMM, YYYY');
                                        }
                                    }
                                }
                                if(!field_map[field_name].order_seq_default) field_map[field_name].order_seq_default = 'desc';
                                break;
                            case 'established_at':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Established Date';
                                if (!field_map[field_name].field_type) {
                                    field_map[field_name].field_type = 'date';
                                }
                                if(!field_map[field_name].order_type) field_map[field_name].order_type = 'date';
                                if(!field_map[field_name].formatted_content) {
                                    field_map[field_name].formatted_content = function (current_value) {
                                        if (current_value) {
                                            return current_object.$dateFormat(current_value, 'DD MMM, YYYY');
                                        }
                                    }
                                }
                                if(!field_map[field_name].order_seq_default) field_map[field_name].order_seq_default = 'desc';
                                break;
                            case 'updated_at':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Updated Date';
                                if (!field_map[field_name].field_type) {
                                    field_map[field_name].field_type = 'date';
                                }
                                if(!field_map[field_name].order_type) field_map[field_name].order_type = 'date';
                                if(!field_map[field_name].formatted_content) {
                                    field_map[field_name].formatted_content = function (current_value) {
                                        if (current_value) {
                                            return current_object.$dateFormat(current_value, 'DD MMM, YYYY');
                                        }
                                    }
                                }
                                if(!field_map[field_name].order_seq_default) field_map[field_name].order_seq_default = 'desc';
                                break;
                            case 'company_admin':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Company Administrator';
                                if(!field_map[field_name].field_type) field_map[field_name].field_type = 'checkbox';
                                break;
                            case 'description':
                                if (!field_map[field_name].field_type) {
                                    field_map[field_name].field_type = 'textarea';
                                }
                                if(!field_map[field_name].formatted_content) {
                                    field_map[field_name].formatted_content = function (current_value) {
                                        if (!current_value) {
                                            return '';
                                        }
                                        return current_value.replace(/(?:\r\n|\r|\n)/g, '<br>');
                                    };
                                }
                                if(!field_map[field_name].order_type) field_map[field_name].order_type = 'text_case_insensitive';
                                if(!field_map[field_name].keywords_search) field_map[field_name].keywords_search = 'word';
                                if(!field_map[field_name].keywords_search_min_char) field_map[field_name].keywords_search_min_char = 3;
                                break;
                            case 'email':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Email';
                                if(!field_map[field_name].field_type) field_map[field_name].field_type = 'text';
                                if(!field_map[field_name].order_type) field_map[field_name].order_type = 'text_case_insensitive';
                                if(!field_map[field_name].order_seq_default) field_map[field_name].order_seq_default = 'asc';
                                if(!field_map[field_name].validate_type) field_map[field_name].validate_type = ['email','repeat'];
                                if(!field_map[field_name].keywords_search) field_map[field_name].keywords_search = 'free_text';
                                if(!field_map[field_name].keywords_search_min_char) field_map[field_name].keywords_search_min_char = 1;
                                break;
                            case 'password':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Password';
                                if(!field_map[field_name].field_type) field_map[field_name].field_type = 'password';
                                if(!field_map[field_name].validate_type) field_map[field_name].validate_type = ['password','repeat'];
                                break;
                            case 'title':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Password';
                                if(!field_map[field_name].field_type) field_map[field_name].field_type = 'select';
                                if (!field_map[field_name].field_available_options) field_map[field_name].field_available_options = [{id:'Mr',name:'Mr'},{id:'Miss',name:'Miss'},{id:'Mrs',name:'Mrs'},{id:'Ms',name:'Ms'},{id:'Dr',name:'Dr'}];
                                break;
                            case 'membership':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Membership';
                                if(!field_map[field_name].field_type) field_map[field_name].field_type = 'select';
                                if (!field_map[field_name].field_available_options) field_map[field_name].field_available_options = [{id:'CPA Australia',name:'CPA Australia'},{id:'The Tax Institute',name:'The Tax Institute'},{id:'National Tax and Accountants Association',name:'National Tax and Accountants Association'},{id:'Institute of Public Accountants',name:'Institute of Public Accountants'},{id:'Association of Taxation and Management Accountants',name:'Association of Taxation and Management Accountants'},{id:'Chartered Accountants Australia and New Zealand',name:'Chartered Accountants Australia and New Zealand'},{id:'Association of Chartered Certified Accountants',name:'Association of Chartered Certified Accountants'},{id:'Association of Financial Advisers',name:'Association of Financial Advisers'},{id:'Australian Bookkeepers Association Ltd',name:'Australian Bookkeepers Association Ltd'},{id:'Financial Planning Association of Australia',name:'Financial Planning Association of Australia'},{id:'Institute of Certified Bookkeepers',name:'Institute of Certified Bookkeepers'},{id:'SMSF Association',name:'SMSF Association'},{id:'TAI Practitioners & Advisers Limited',name:'TAI Practitioners & Advisers Limited'},{id:'Law society of NSW',name:'Law society of NSW'},{id:'Law Institute of Victoria',name:'Law Institute of Victoria'},{id:'Queensland Law Society',name:'Queensland Law Society'},{id:'ACT Law Society',name:'ACT Law Society'},{id:'Law Society of Tasmania',name:'Law Society of Tasmania'},{id:'Law Society of South Australia',name:'Law Society of South Australia'},{id:'Law Society Northern Territory',name:'Law Society Northern Territory'},{id:'Law Society of Western Australia',name:'Law Society of Western Australia'},{id:'Other',name:'Other'}];
                                break;
                            case 'registered':
                                if(!field_map[field_name].display_name) field_map[field_name].display_name = 'Registered';
                                if(!field_map[field_name].formatted_content) field_map[field_name].formatted_content = {0:'<span class="error_text">Pending</span>',1:'<span class="success_text">Completed</span>'};
                                // if(!field_map[field_name].formatted_content) {
                                //     field_map[field_name].formatted_content = function (value) {
                                //         let return_value = '';
                                //         switch (value) {
                                //             case 0:
                                //                 return_value = '<span class="error_text">Pending</span>';
                                //                 break;
                                //             case 1:
                                //                 return_value = '<span class="success_text">Completed</span>';
                                //                 break;
                                //         }
                                //         return return_value;
                                //     }
                                // }
                                if(!field_map[field_name].order_seq_default) field_map[field_name].order_seq_default = 'asc';
                                if (typeof field_map[field_name].default_value === 'undefined') {
                                    field_map[field_name].default_value = 0;
                                }
                                break;
                            case 'representative_ids':
                                // $row['field_type'] = 'subform_multi';
                                // $row['ref_name'] = 'representative';
                                // $row['reversed_ref_name'] = 'joint_member';
                                // $row['ref_module_name'] = $module;
                                if(!field_map[field_name].field_type) field_map[field_name].field_type = 'subform_multi';
                                if(!field_map[field_name].ref_name) field_map[field_name].ref_name = 'representative';
                                if(!field_map[field_name].reversed_ref_name) field_map[field_name].reversed_ref_name = 'joint_member';
                                if(!field_map[field_name].ref_module_name) field_map[field_name].ref_module_name = 'bgl_contact';
                                break;
                            case 'name':
                            case 'display_name':
                            case 'first_name':
                            case 'last_name':
                            case 'long_name':
                            case 'formatted_name':
                                if(!field_map[field_name].order_type) field_map[field_name].order_type = 'text_case_insensitive';
                                if(!field_map[field_name].keywords_search) field_map[field_name].keywords_search = 'word';
                                if(!field_map[field_name].keywords_search_min_char) field_map[field_name].keywords_search_min_char = 3;
                                break;
                            case 'capital_ld_proportion':
                            case 'income_ld_proportion':
                                if(!field_map[field_name].formatted_content) field_map[field_name].formatted_content = function (current_value) {
                                    // return $root.fix_format(current_value,[{type:'decimal',precision:2,prefix:''}]);
                                    return current_object.fix_format(current_value,[{type:'decimal',precision:0,postfix:'%'}]);
                                };
                                break;
                            default:
                                if(!field_map[field_name].order_seq_default) field_map[field_name].order_seq_default = 'asc';
                                if (!field_map[field_name]['ref_name'] && /_id$/.test(field_name)) {
                                    let ref_module_name = field_name.substring(0, field_name.length - 3);
                                    field_map[field_name]['ref_name'] = ref_module_name;
                                    if(!field_map[field_name].keywords_search) field_map[field_name].keywords_search = 'formatted_content';
                                    if(!field_map[field_name].display_name) {
                                        let display_name = '';
                                        let name_words = ref_module_name.split('_');
                                        name_words.forEach(function (name_word) {
                                            if (!name_word.length) return;
                                            name_word = name_word.trim();
                                            if (name_word === 'id') return;
                                            display_name += name_word.charAt(0).toUpperCase() + name_word.slice(1) + ' ';
                                        });
                                        display_name = display_name.trim();
                                        field_map[field_name].display_name = display_name;
                                    }
                                }
                                if(!field_map[field_name].display_name) {
                                    let display_name = '';
                                    let name_words = field_name.split('_');
                                    name_words.forEach(function (name_word) {
                                        if (!name_word.length) return;
                                        name_word = name_word.trim();
                                        if (name_word === 'id') return;
                                        display_name += name_word.charAt(0).toUpperCase() + name_word.slice(1) + ' ';
                                    });
                                    display_name = display_name.trim();
                                    field_map[field_name].display_name = display_name;
                                }
                        }

                        if (!field_map[field_name].field_type) {
                            field_map[field_name].field_type = 'text';
                        } else {
                            if (field_map[field_name].field_type === 'select') {
                                if (!field_map[field_name].field_available_options) {
                                    field_map[field_name].field_available_options = [];
                                }
                            }
                        }
                        if (field_map[field_name].field_available_options) {
                            let result = [];
                            Object.keys(field_map[field_name].field_available_options).forEach(function (option_row_index) {
                                let option_row = field_map[field_name].field_available_options[option_row_index];
                                if (isNaN(parseFloat(option_row_index))) {
                                    if (typeof option_row === 'string') {
                                        option_row = {id:option_row_index,name:option_row};
                                    } else {
                                        option_row.id = option_row_index;
                                    }
                                } else {
                                    // if index is numeric, source list is an array
                                    if (typeof option_row === 'string') {
                                        option_row = {id:option_row,name:option_row};
                                    }
                                }
                                result.push(option_row);
                            });
                            field_map[field_name].field_available_options = result;
                        }
                        if (!field_map[field_name].order_type) {
                            field_map[field_name].order_type = 'text';
                        }
                        if (!field_map[field_name].order_seq_default) {
                            field_map[field_name].order_seq_default = 'asc';
                        }
                        if (!field_map[field_name].validate_type) {
                            field_map[field_name].validate_type = [];
                        }
                        // parent_id FK should create _ref_list_children (not _ref_list_<module_name>)
                        if (field_name === 'parent_id' && !field_map[field_name].reversed_ref_name) {
                            field_map[field_name].reversed_ref_name = 'children';
                        }
                    //                            'order_seq_default'=>'asc',
                        //                         'field_type'=>'text',
                        //                         'order_type'=>'text',
                        //                         'validate_type'=>[],
                    });
                    break;
                default:
                    console.log('allen debug set_field with unrecognised format',fields);
            }
        },
        // extend function for set_field, choose default field map object (create blank one if does not exist)
        set_module_field: function(fields, module_name, data_set) {
            let current_object = this;
            let update_fields = {};
            if (!data_set.item_field[module_name]) {
                data_set.item_field[module_name] = {};
            }
            if (!data_set.item_field[module_name]['_list']) {
                data_set.item_field[module_name]['_list'] = {};
            }
            if (!data_set.item_field[module_name]['_list']['all']) {
                data_set.item_field[module_name]['_list']['all'] = [];
            }

            switch (typeof fields) {
                case 'string':
                    fields = fields.split(',');
                case 'object':
                    if (Array.isArray(fields)) {
                        let fields_object = {};
                        fields.forEach(function (field_name) {
                            if (typeof field_name === 'string') {
                                fields_object[field_name] = {name: field_name};
                            }
                            if (typeof field_name === 'object' && field_name['name']) {
                                fields_object[field_name['name']] = Object.assign({}, field_name);
                            }
                        });
                        fields = fields_object;
                    }
                    Object.keys(fields).forEach(function (field_name) {
                        if (/^_/.test(field_name)) {
                            return false;       // Ignore fields lead by _, e.g. _order_id
                        }
                        if (data_set.item_field[module_name]['_list']['all'].indexOf(field_name) === -1) {
                            data_set.item_field[module_name]['_list']['all'].push(field_name);
                        }
                        if (!data_set.item_field[module_name][field_name]) {
                            update_fields[field_name] = fields[field_name];
                        }
                        if (data_set.item_field[module_name]['_list']['attached']) {
                            data_set.item_field[module_name]['_list']['attached'].forEach(function (field_name) {
                                if (!data_set.item_field[module_name][field_name]) {
                                    update_fields[field_name] = {name:field_name,flag_field:'attached',default_value:'',ref_name:module_name+'_data_attached'};
                                }
                            });
                        }
                        if (Object.keys(update_fields).length) {
                            // console.log('allen debug set_module_field',module_name,JSON.stringify(Object.keys(update_fields)));
                            current_object.set_field(update_fields, data_set.item_field[module_name]);
                        }
                    });
                    break;
                default:
                    console.log('set_module_field encounter unexpected fields type',typeof fields);
            }

            Object.keys(data_set.item_field[module_name]).forEach(function (field_name) {
                if (data_set.item_field[module_name][field_name].flag_field) {
                    if (!data_set.item_field[module_name]['_list'][data_set.item_field[module_name][field_name].flag_field]) {
                        data_set.item_field[module_name]['_list'][data_set.item_field[module_name][field_name].flag_field] = [];
                    }
                    if (data_set.item_field[module_name]['_list'][data_set.item_field[module_name][field_name].flag_field].indexOf(field_name) === -1) {
                        data_set.item_field[module_name]['_list'][data_set.item_field[module_name][field_name].flag_field].push(field_name);
                    }
                }
            });
            if (data_set.item_field[module_name]['parent_id']) {
                data_set.item_field[module_name]['parent_id'].ref_module_name = module_name;
            }
            if (/^bgl_/.test(module_name)) {
                Object.keys(data_set.item_field[module_name]).forEach(function(field_name) {
                    let current_field = data_set.item_field[module_name][field_name];
                    // if (current_field.ref_name && current_field.flag_field === 'fillable') {
                    if (current_field.ref_name) {
                        if (!/^bgl_/.test(current_field.ref_name) && current_field.ref_name !== 'user') {
                            current_field.ref_name = 'bgl_'+current_field.ref_name;
                        }
                        switch (current_field.ref_name) {
                            case 'bgl_shareholder':
                            case 'bgl_beneficial_owner':
                                current_field.ref_module_name = 'bgl_contact';
                                break;

                        }
                    }
                });
            }
        },
        set_item_field: function(item_field, data_set) {
            // Set up item_field from item_field object
            let current_object = this;
            if (typeof data_set === 'undefined') {
                data_set = current_object;
            }
            if (!data_set.item_field) {
                data_set.item_field = {};
            }
            Object.keys(item_field).forEach(function (module_name) {
                current_object.set_module_field(item_field[module_name], module_name, data_set);
            });
        },
        set_item_list_field: function(item_list, data_set) {
            // Set up item_field from item_list object
            let current_object = this;
            let field_name_list = [];
            if (typeof data_set === 'undefined') {
                data_set = current_object;
            }
            if (!data_set.item_field) {
                data_set.item_field = {};
            }
            Object.keys(item_list).forEach(function (module_name) {
                if (item_list[module_name].length) {
                    current_object.set_module_field(Object.keys(item_list[module_name][0]), module_name, data_set);
                }
            });
        },

        /*set_relationship: function(items, global_map, module_name, module_fields, additional_fields) {
            let current_object = this;
            let result = [];
            if (!items || !items.length)
                console.log('allen debug set_relationship',items, global_map, module_name, module_fields);
            items.forEach(function (item) {
                if (!module_fields) {
                    module_fields = current_object.set_field(Object.keys(item), current_object);
                }
                Object.keys(module_fields).forEach(function (field_name) {
                    let field = module_fields[field_name];
                    if (field._ref) {
                        let _ref = field._ref;
                        if (!_ref[item[field.name]]) {
                            if (!item[field.name]) return false;
                            delete item[field.ref_name];
                            console.log('Map '+field.ref_name+' cannot find referenced item by id: '+item[field.name]);
                            console.log(field._ref,field.name,item);
                            return false
                        }
                        item[field.ref_name] = _ref[item[field.name]];
                        if (module_name) {
                            if (!_ref[item[field.name]][module_name+'_list']) {
                                _ref[item[field.name]][module_name+'_list'] = [];
                            }
                            if (item.deleted) {
                                if (_ref[item[field.name]][module_name+'_list'].indexOf(item) !== -1) {
                                    _ref[item[field.name]][module_name+'_list'].splice(_ref[item[field.name]][module_name+'_list'].indexOf(item),1);
                                }
                            } else {
                                if (_ref[item[field.name]][module_name+'_list'].indexOf(item) === -1) {
                                    _ref[item[field.name]][module_name+'_list'].push(item);
                                }
                            }
                        }
                    }
                    if (field.order_type) {
                        // If field is not ordered by alphabetical order, set up extra field for index
                        switch (field.order_type) {
                            case 'date':
                                // See timezone-normalisation note in set_module_relationship.
                                item['_order_'+field_name] = new Date(
                                    (typeof item[field_name] === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/.test(item[field_name]))
                                        ? item[field_name].replace(' ', 'T') + 'Z'
                                        : item[field_name]
                                );
                                break;
                            case 'number':
                                item['_order_'+field_name] = parseFloat(item[field_name]);
                                break;
                            case 'text_case_insensitive':
                                item['_order_'+field_name] = item[field_name].toLowerCase();
                                break;

                        }
                    }
                    if (field.flag_data_attachment) {
                        // if (typeof item[field_name] === 'undefined') {
                        //     item[field_name] = field.default_value;
                        // }
                        item[field_name] = field.default_value;
                    }
                });
                if (additional_fields && additional_fields.length) {
                    additional_fields.forEach(function (field_name) {
                        if (typeof item[field_name] === 'undefined') {
                            item[field_name] = '';
                        }
                    });
                }

                if (item['data_attachment_id'] && global_map['data_attachment'][item['data_attachment_id']]) {
                    Object.keys(item['data_attachment'].content).forEach(function (additional_data_key) {
                        item[additional_data_key] = item['data_attachment'].content[additional_data_key];
                    });
                } else {
                    // When whole data_attachment deleted, force reset all
                    // Object.keys(module_fields).forEach(function (field_name) {
                    //     let field = module_fields[field_name];
                    //     if (field.flag_data_attachment) {
                    //         item[field_name] = field.default_value;
                    //     }
                    // };
                }
                result.push(item);
            });
            return result;
        },*/
        set_field_available_options_by_ref: function(field) {
            if (!field.field_available_options) {
                field.field_available_options = [];
            } else {
                field.field_available_options.splice(0, field.field_available_options.length);
            }
            if (!field._ref) {
                return;
            }
            let new_option_list = [];
            Object.keys(field._ref).forEach(function (ref_id) {
                let option = {
                    id: ref_id
                };
                if (field._ref[ref_id].formatted_name) {
                    option.name = field._ref[ref_id].formatted_name;
                } else {
                    if (field._ref[ref_id].formatted_address) {
                        option.name = field._ref[ref_id].formatted_address;
                    } else {
                        if (field._ref[ref_id].name) {
                            option.name = field._ref[ref_id].name;
                        } else {
                            option.name = ref_id;
                        }
                    }
                }
                if (field._ref[ref_id].long_name) {
                    option.name = field._ref[ref_id].long_name;
                }
                option.name_lower = option.name.toLowerCase();
                new_option_list.push(option);
            });
            let sorted_option_list = _.orderBy(new_option_list, 'name_lower', 'asc');

            sorted_option_list.forEach(function (option) {
                field.field_available_options.push(option);
            });
        },
        set_module_relationship: function(items, module_name, data_set) {
            // Alt for set_relationship, can only be used after initiation
            let current_object = this;
            if (typeof data_set === 'undefined') {
                data_set = current_object;
            }
            if (module_name === 'data_attachment') {
                // If only set data_attachment relationship, data_attachment itself is not affected, but the referenced items are. Create a dummy item_list to solve this.
                let item_list = {data_attachment:items};
                current_object.set_item_list_relationship(item_list,data_set);
                return items;
            }
            if (!items || !items.length) {
                return items;
            }
            if (!data_set.item_field[module_name]) {
                current_object.set_module_field(Object.keys(items[0]), module_name, data_set);
            }
            if (data_set.item_field[module_name]._ref_by) {
                data_set.item_field[module_name]._ref_by.forEach(current_object.set_field_available_options_by_ref);
            }
            items.forEach(function (item) {
                if (!item.deleted && data_set.item_map[module_name][item.id] !== item) {
                    console.log('Warning: set_module_relationship try to set item does not belong to current item_map',item,data_set.item_map[module_name][item.id]);
                    return;
                }
                Object.keys(data_set.item_field[module_name]).forEach(function (field_name) {
                    let field = data_set.item_field[module_name][field_name];
                    if (field.order_type) {
                        // If field is not ordered by alphabetical order, set up extra field for index
                        let field_value = item[field_name];
                        if (typeof field_value !== 'undefined') {
                            switch (field.order_type) {
                                case 'date':
                                    if (field_value === null) {
                                        field_value = '1970-01-01';
                                        item[field_name] = '';
                                    }
                                    // Server datetimes are UTC. Eloquent serializes with an explicit
                                    // 'Z' (e.g. '2026-05-05T02:38:59.000000Z'), but raw DB::table
                                    // queries (used by get_item after order_trust/order_super
                                    // submits) return unzoned 'YYYY-MM-DD HH:MM:SS'. new Date()
                                    // parses the unzoned form as LOCAL time, sorting freshly-saved
                                    // rows ~11h behind the page-load rows in Sydney. Force UTC.
                                    item['_order_'+field_name] = new Date(
                                        (typeof field_value === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/.test(field_value))
                                            ? field_value.replace(' ', 'T') + 'Z'
                                            : field_value
                                    );
                                    break;
                                case 'number':
                                    if (field_value === null) {
                                        field_value = '0';
                                        item[field_name] = '0';
                                    }
                                    item['_order_'+field_name] = parseFloat(field_value);
                                    break;
                                case 'text_case_insensitive':
                                    if (field_value === null) {
                                        field_value = '';
                                        item[field_name] = '';
                                    }
                                    item['_order_'+field_name] = field_value.toLowerCase();
                                    break;

                            }
                        }
                    }
                    if (field.flag_field === 'attached') {
                        // attached fields are by default 'undefined', give a default value to avoid causing rendering error for v-model
                        item[field_name] = field.default_value;
                    }
                    if (module_name === 'register_company_party' && field_name === 'formatted_name') {
                        field.formatted_content = function (row) { return current_object.$root.fix_format(row,['register_company_party']);};
                    }
                    if (field.field_available_options) {
                        field.formatted_content = {};
                        if (typeof field.empty_value !== 'undefined') {
                            field.formatted_content[field.empty_value] = '';
                        }
                        field.field_available_options.forEach(function (available_option) {
                            field.formatted_content[available_option.id] = available_option.name;
                        });
                    }
                    if (field.ref_name) {
                        // console.log('allen debug set_module_relationship',module_name,field_name,field.ref_name);
                        if (field._ref !== data_set.item_map[field.ref_name]) {
                            delete field._ref;
                        }
                        if (!field._ref) {
                            // switch (field.ref_name) {
                            //     case 'parent':
                            //         field.ref_name = module_name;
                            //         break;
                            // }
                            let _ref = data_set.item_map[field.ref_name];
                            if (data_set.item_field[field.ref_name] && field.ref_name !== 'address' && field.ref_name !== 'data_attachment') {
                                if (!data_set.item_field[field.ref_name]._ref_by) {
                                    data_set.item_field[field.ref_name]._ref_by = [];
                                }
                                current_object.$root.add_to_list(field, data_set.item_field[field.ref_name]._ref_by);
                            }
// console.log('allen debug _ref',_ref,data_set,data_set === this.$root);
                            if (field.ref_module_name) {
                                // Some FK may have a different ref_name from the referenced module,
                                // in this case make sure ref_module_name is predefined and reference to the correct module,
                                // e.g. parent_id refer to current module itself, bgl_company->contact_id refer to bgl_contact
                                _ref = data_set.item_map[field.ref_module_name];
                            }
                            if (!_ref) {
                                // e.g. director_id, is not really a FK, does not have a referenced table
                                return false;
                            }
                            field._ref = _ref;
                            if (field.ref_name !== 'bgl_company') {
                                field.default_value = 0;
                                field.empty_value = 0;
                            }
                            field.formatted_content = function (ref_id) {
// console.log('allen debug ref_id',ref_id,typeof ref_id,_ref[ref_id]);
                                if (!ref_id) {
                                    if (typeof ref_id === 'object') {
                                        return '-- N/A --';
                                    } else {
                                        if (ref_id === 0) {
                                            return 'N/A';
                                        }
                                        return ref_id;
                                    }
                                }
                                if (typeof ref_id === 'object') {
                                    if (typeof ref_id.length !== 'undefined') {
                                        // If ref_id is an array []
                                        let result = [];
                                        ref_id.forEach(function (ref_id) {
                                            if (_ref[ref_id] && _ref[ref_id].formatted_name) {
                                                result.push('"'+_ref[ref_id].formatted_name+'"');
                                            } else {
                                                result.push('['+ref_id+']');
                                            }
                                        });
                                        return '['+result.join(',')+']';
                                    } else {
                                        return JSON.stringify(ref_id);
                                    }
                                } else {
                                    if (_ref[ref_id]) {
                                        return _ref[ref_id].formatted_name||_ref[ref_id].formatted_address||_ref[ref_id].name||ref_id;
                                    } else {
                                        return ref_id;
                                    }
                                }
                            };
                            if (field.field_type === 'text' && field.flag_field !== 'attached') {
                                field.field_type = 'select';
                                current_object.$root.set_field_available_options_by_ref(field);
                            }

                        }
                        if (!item[field.name]) {
                            // e.g. company_id in user is optional, can be 0 when user does not belong to any company
                            return false;
                        }
                        if (Array.isArray(item[field.name])) {
                            if (item.deleted) {
                                item[field.name].forEach(function (ref_id) {
                                    let ref_item = field._ref[ref_id];

                                    delete ref_item['_ref_'+field.reversed_ref_name];
                                });
                            } else {
                                if (!item['_ref_list_'+field.ref_name]) {
                                    item['_ref_list_'+field.ref_name] = [];
                                }
                                item[field.name].forEach(function (ref_id) {
                                    let ref_item = field._ref[ref_id];

                                    item['_ref_list_'+field.ref_name].push(ref_item);
                                    ref_item['_ref_'+field.reversed_ref_name] = item;
                                });
                            }
                        } else {
                            if (!field._ref[item[field.name]]) {
                                // fk does not exist in referenced table. either the fk is wrong (e.g. company_id:'new_1') or the referenced item is not loaded properly (or deleted),
                                // In this case, delete the original referenced item as it is not consistant with fk any more
                                delete item['_ref_'+field.ref_name];
                                // console.log('Map '+field.ref_name+' cannot find referenced item by id: '+item[field.name], module_name, field);
                                return false
                            }
                            let ref_item = field._ref[item[field.name]];
                            item['_ref_'+field.ref_name] = ref_item;
                            if (!field.reversed_ref_name) {
                                field.reversed_ref_name = module_name;
                            }
                            // set up one-to-many backwards reference, e.g. company_object['_ref_list_user'] = [user_object1,user_object2,user_object3...]
                            if (!ref_item['_ref_list_'+field.reversed_ref_name]) {
                                ref_item['_ref_list_'+field.reversed_ref_name] = [];
                            }
                            if (item.deleted) {
                                if (ref_item['_ref_list_'+field.reversed_ref_name].indexOf(item) !== -1) {
                                    ref_item['_ref_list_'+field.reversed_ref_name].splice(ref_item['_ref_list_'+field.reversed_ref_name].indexOf(item),1);
                                }
                            } else {
// console.log('allen debug set_module_relationship item belong to map?',module_name,item.id,data_set.item_map[module_name][item.id] === item);
                                if (ref_item['_ref_list_'+field.reversed_ref_name].indexOf(item) === -1) {
                                    ref_item['_ref_list_'+field.reversed_ref_name].push(item);
                                }
                            }
                        }
                    }
                });

                if (item.reference_type && item.reference_id && data_set.item_map && data_set.item_map[item.reference_type] && data_set.item_map[item.reference_type][item.reference_id] ) {
                    let _ref = data_set.item_map[item.reference_type];
                    if (data_set.item_field[item.reference_type]) {
                        if (!data_set.item_field[item.reference_type]._ref_by) {
                            data_set.item_field[item.reference_type]._ref_by = [];
                        }
                        // current_object.$root.add_to_list(field, data_set.item_field[item.reference_type]._ref_by);
                    }
                    let field = data_set.item_field[module_name]['reference_id'];
                    if (field && !field['reversed_ref_name']) {
                        field['reversed_ref_name'] = module_name;
                    }
                    let ref_item = _ref[item.reference_id];
                    if (ref_item) {
                        item['_ref_'+item.reference_type] = _ref[item.reference_id];
                        if (!ref_item['_ref_list_'+field.reversed_ref_name]) {
                            ref_item['_ref_list_'+field.reversed_ref_name] = [];
                        }
                        if (item.deleted) {
                            if (ref_item['_ref_list_'+field.reversed_ref_name].indexOf(item) !== -1) {
                                ref_item['_ref_list_'+field.reversed_ref_name].splice(ref_item['_ref_list_'+field.reversed_ref_name].indexOf(item),1);
                            }
                        } else {
// console.log('allen debug set_module_relationship item belong to map?',module_name,item.id,data_set.item_map[module_name][item.id] === item);
                            if (ref_item['_ref_list_'+field.reversed_ref_name].indexOf(item) === -1) {
                                if (typeof item.priority === 'undefined') {
                                    ref_item['_ref_list_'+field.reversed_ref_name].push(item);
                                } else {
                                    let current_index = 0;
                                    while (current_index <= ref_item['_ref_list_'+field.reversed_ref_name].length) {
                                        if (typeof ref_item['_ref_list_'+field.reversed_ref_name][current_index] === 'undefined' || ref_item['_ref_list_'+field.reversed_ref_name][current_index].priority > item.priority) {
                                            ref_item['_ref_list_'+field.reversed_ref_name].splice(current_index-1,0,item);
                                            break;
                                        }
                                        current_index++;
                                    }
                                }
                            }
                        }
                    }

//                     if (!field._ref[item[field.name]]) {
//                         // fk does not exist in referenced table. either the fk is wrong (e.g. company_id:'new_1') or the referenced item is not loaded properly (or deleted),
//                         // In this case, delete the original referenced item as it is not consistant with fk any more
//                         delete item['_ref_'+field.ref_name];
//                         // console.log('Map '+field.ref_name+' cannot find referenced item by id: '+item[field.name], module_name, field);
//                         return false
//                     }
//                     let ref_item = field._ref[item[field.name]];
//                     item['_ref_'+field.ref_name] = ref_item;
//                     if (!field.reversed_ref_name) {
//                         field.reversed_ref_name = module_name;
//                     }
//                     // set up one-to-many backwards reference, e.g. company_object['_ref_list_user'] = [user_object1,user_object2,user_object3...]
//                     if (!ref_item['_ref_list_'+field.reversed_ref_name]) {
//                         ref_item['_ref_list_'+field.reversed_ref_name] = [];
//                     }
//                     if (item.deleted) {
//                         if (ref_item['_ref_list_'+field.reversed_ref_name].indexOf(item) !== -1) {
//                             ref_item['_ref_list_'+field.reversed_ref_name].splice(ref_item['_ref_list_'+field.reversed_ref_name].indexOf(item),1);
//                         }
//                     } else {
// // console.log('allen debug set_module_relationship item belong to map?',module_name,item.id,data_set.item_map[module_name][item.id] === item);
//                         if (ref_item['_ref_list_'+field.reversed_ref_name].indexOf(item) === -1) {
//                             ref_item['_ref_list_'+field.reversed_ref_name].push(item);
//                         }
//                     }
                }

                if (data_set.item_map && data_set.item_map[module_name+'_data_attached'] && data_set.item_map[module_name+'_data_attached'][item.id]) {
// console.log('data_set.item_map[module_name+\'_data_attached\'][item.id]',module_name,item.id,data_set.item_map[module_name+'_data_attached'][item.id]);
                    Object.keys(data_set.item_map[module_name+'_data_attached'][item.id]).forEach(function (additional_data_key) {
                        item[additional_data_key] = data_set.item_map[module_name+'_data_attached'][item.id][additional_data_key];
                    });
                }
            });
            if (module_name === 'register_company_party') {
                // Generate formatted_name, address_id for "Joint Member", "Trust" and "Super" type register_company_party
                items.forEach(function (item) {
                    if (['Joint Member','Trust','Super'].indexOf(item.type) >= 0) {
                        if (!item.name) {
                            if (item._ref_list_children) {
                                let representative_name_list = [];
                                let beneficial_owner_name = '';
                                item._ref_list_children.forEach(function(sub_item) {
                                    if (sub_item.class === 'representative') {
                                        representative_name_list.push(sub_item.formatted_name);
                                        if (!item.address_id && sub_item._ref_address) {
                                            item.address_id = sub_item.address_id;
                                            item._ref_address = sub_item._ref_address;
                                        }
                                    }
                                    if (sub_item.class === 'beneficial_owner') {
                                        beneficial_owner_name = sub_item.formatted_name;
                                    }
                                });
                                if (representative_name_list.length) {
                                    item.name = representative_name_list.pop();
                                    if (representative_name_list.length) {
                                        item.name = representative_name_list.join(', ') + ' and ' + item.name;
                                    }
                                }
                                if (beneficial_owner_name) {
                                    item.name += ' as trustee for ' + beneficial_owner_name;
                                }
                                if (!item.formatted_name) {
                                    item.formatted_name = item.name;
                                }
                            }
                        }
                        if (!item.address_id && item._ref_parent) {
                            item.address_id = item._ref_parent.address_id;
                            item._ref_address = item._ref_parent._ref_address;
                        }
                    }

                });
            }
            return items;
        },
        set_item_list_relationship: function(item_list, data_set) {
            let current_object = this;
            if (typeof data_set === 'undefined') {
                data_set = current_object;
            }
            if (item_list.data_attachment) {
                // In case only data_attachment updated, add the referenced item to the result item_list to update its attached fields
                item_list.data_attachment.forEach(function (data_row) {
                    if (!data_set.item_map || !data_set.item_map[data_row.reference_type]) {
                        // console.log('Error on setting data_attachment, reference_type does not exist in current item_map',data_row.reference_type,data_set.item_map);
                        return false;
                    }
                    if (!item_list[data_row.reference_type]) {
                        item_list[data_row.reference_type] = [];
                    }
                    let referenced_item = data_set.item_map[data_row.reference_type][data_row.reference_id];
                    if (!referenced_item) {
                        // If referenced item not in the map, skip; For example, current page has only loaded current user, ignore other users' attached data
                        // console.log('Error on setting data_attachment, reference_id does not exist',data_row.reference_id,data_row.reference_type,data_set.item_map);
                        return false;
                    }
                    if (item_list[data_row.reference_type].indexOf(referenced_item) === -1) {
                        item_list[data_row.reference_type].push(referenced_item);
                    }
                });
            }
            Object.keys(item_list).forEach(function (module_name) {
                if (module_name === 'data_attachment') {
                    return false;
                }
                current_object.set_module_relationship(item_list[module_name], module_name, data_set);
            });
            return item_list;
        },

//         set_module_relationship: function(items, module_name, data_set) {
// console.log('allen debug set_relationship',items, module_name);
//             // Alt for set_relationship, can only be used after initiation
//             let current_object = this;
//             if (typeof data_set === 'undefined') {
//                 data_set = current_object;
//             }
//             let result = [];
//             switch (module_name) {
//                 case 'data_attachment':
//                     break;
//                 case 'trust':
//                 default:
//                     let attached_field = null;
//                     if (data_set.item_map.attached_field && data_set.item_map.attached_field[module_name]) {
//                         attached_field = data_set.item_map.attached_field[module_name];
//                     }
// console.log('attached_field',attached_field);
//                     result = current_object.set_relationship(items, data_set.item_map, module_name, data_set.item_field[module_name], attached_field);
//             }
//             return result;
//         },
        set_page: function (page_id, uri_query, option) {
            // page_id can be either numeric id such as 201, 302 or full uri_pathname such as /client/trusts, /admin/register-companies
            let current_object = this;
            current_object.flag_loading++;
            if (!page_id) {
                page_id = current_object.current_id.page;
            }
console.log('allen debug set_page',current_object.flag_loading);
            if (typeof uri_query === 'object' && !$.isEmptyObject(uri_query)) {
                uri_query = '?'+$.param(uri_query);
            }
            if (typeof uri_query !== 'string') {
                uri_query = '';
            } else {
                if (uri_query.length && uri_query.charAt(0) !== '?') {
                    uri_query = '?'+uri_query;
                }
            }
            if (typeof option === 'undefined') {
                option = {update_state:'pushState',scroll_top:true};
            }
            // current_object.current_page = page;
            let current_page = null;
            if (current_object.item_map['page'][page_id]) {
                current_page = current_object.item_map['page'][page_id];
            } else {
                if (current_object.item_map['uri_pathname'][page_id]) {
                    current_page = current_object.item_map['uri_pathname'][page_id];
                }
            }
console.log('allen debug set_page a',page_id,current_page);
            if (current_page) {
                current_object.current_id['page'] = current_page.id;
                if (option.update_state) {
                    let url = current_page.pathname + uri_query;
                    history[option.update_state]({}, '', url);
                }
                if (option.scroll_top) {
                    // $('#app').scrollTop(0);
                    // $('html, body').stop().animate({
                    //     scrollTop:$('#app').offset().top
                    // }, 550, 'swing');
                    $('html, body').scrollTop($('#app').offset().top);
                }
                $('body').removeClass('mobile_menu_open');

                current_object.page_component_id++;
            }
            current_object.flag_loading--;
        },
        page_component: function () {
            let page = this.item_map.page[this.current_id.page];
            // console.log('page_component',page,page.component_name,new Date().getTime());
            if (page && page.component_name) {
                return page.component_name;
            }
            return 'client-not-found';
        },
        page_option: function (page_id) {
            let page = this.item_map['page'][page_id];
            if (!page) {
                return {};
            }
            let current_object = this;
            let page_option = {};
            if (page.option) {
                page_option = page.option;
            }
            return page_option;
        },
        toggle_menu: function () {
            $('body').toggleClass('mobile_menu_open')
        },
        uri_query_decoder: function() {
            let uri_param = {};
            if (location.search) {
                let param_set = location.search.substring(1).split("&");
                param_set.forEach(function (param_string) {
                    if (param_string === '') return;
                    let param_string_parts = param_string.split('=');
                    uri_param[decodeURIComponent(param_string_parts[0])] = decodeURIComponent(param_string_parts[1]);
                });
            }
            console.log('allen debug uri_param',uri_param);
            return uri_param;
        },
        fix_format: function(input_value,option) {
            if (typeof input_value === 'undefined') {
                return '';
            }
            if (typeof option === 'undefined') {
                option = ['trim'];
            }
            let current_object = this;
            let result = input_value;
            // option.forEach(function (option_row) {
            for (let option_row_index=0; option_row_index<option.length; option_row_index++) {
                let option_row = option[option_row_index];
                let flag_end_loop = false;

                if (typeof option_row === 'string') {
                    option_row = {type: option_row};
                }
                if (!option_row.type) {
                    return;
                }
                switch (option_row.type) {
                    case 'address':
                        let formatted_address_stack = [];
                        let formatted_address_line = '';

                        if (input_value.route) {
                            formatted_address_line = input_value.route;
                            if (input_value.street_number) {
                                formatted_address_line = input_value.street_number + ' ' + formatted_address_line;
                            }
                            if (input_value.subpremise) {
                                formatted_address_line = input_value.subpremise + '/' + formatted_address_line;
                            }
                            formatted_address_stack.push(formatted_address_line);
                        }

                        if (input_value.locality) {
                            formatted_address_line = input_value.locality;
                            switch (input_value.country) {
                                case 'NZ':
                                    // New Zealand omit administrative_area_level_1 in formatted address
                                    break;
                                default:
                                    if (input_value.administrative_area_level_1) {
                                        formatted_address_line += ' ' + input_value.administrative_area_level_1;
                                    }
                            }

                            if (input_value.postal_code) {
                                formatted_address_line += ' ' + input_value.postal_code;
                            }
                            formatted_address_stack.push(formatted_address_line);
                        }

                        if (!formatted_address_stack.length) {
                            input_value.formatted_address = '';
                            return input_value.formatted_address;
                        }

                        if (input_value.country) {
                            if (current_object.item_map && current_object.item_map.country && current_object.item_map.country[input_value.country]) {
                                formatted_address_stack.push(current_object.item_map.country[input_value.country].name);
                            } else {
                                formatted_address_stack.push(input_value.country);
                            }
                        }

                        result = formatted_address_stack.join(', ');
                        break;
                    case 'bgl_address':
                        formatted_address_stack = [];
                        ['street_line1', 'street_line2', 'care_of', 'suburb', 'state', 'postcode'].forEach(function (key) {
                            if (input_value[key]) {
                                formatted_address_stack.push(input_value[key]);
                            }
                        });

                        result = formatted_address_stack.join(', ');

                        break;
                    case 'bgl_contact':
                        switch (input_value.contact_type) {
                            case 'People':
                                result = input_value.last_name;
                                if (input_value.middle_name) {
                                    result = input_value.middle_name + ' ' + result;
                                }
                                if (input_value.first_name) {
                                    result = input_value.first_name + ' ' + result;
                                }
                                if (input_value.title) {
                                    result = input_value.title + ' ' + result;
                                }
                                break;
                            case 'Joint Member':
                                if (typeof input_value.representative_ids === 'string') {
                                    input_value.representative_ids = JSON.parse(input_value.representative_ids);
                                }
                                let name_stack = [];
                                input_value.representative_ids.forEach(function (contact_id) {
                                    name_stack.push(current_object.fix_format(current_object.item_map.bgl_contact[contact_id], ['bgl_contact']));
                                });
                                result = 'Joint Member: ' + name_stack.join(', ') + '';
                                break;
                            case 'Company':
                            default:
                                result = input_value.name;
                                if (input_value.business_number) {
                                    result += ' (A.B.N. ' + input_value.business_number + ')';
                                } else {
                                    if (input_value.company_number) {
                                        result += ' (A.C.N. ' + input_value.company_number + ')';
                                    }
                                }
                                break;
                        }
                        break;
                    case 'date':
                        result = current_object.$dateFormat(result, 'DD MMM, YYYY');
                        break;
                    case 'decimal':
                        if (!option_row.precision || option_row.precision < 0 || option_row.precision > 100) {
                            option_row.precision = 0;
                        }
                        if (!option_row.negative_prefix) {
                            option_row.negative_prefix = '-';
                        }
                        if (!option_row.positive_prefix) {
                            option_row.positive_prefix = '';
                        }
                        if (typeof result === 'string') {
                            result = result.replace(/[^\d.]/ig, '');
                        }
                        let result_value = parseFloat(result);
                        result = result_value.toFixed(option_row.precision);
                        if (isNaN(result)) {
                            result = '';
                        } else {
                            if (option_row.prefix) {
                                if (result_value < 0) {
                                    result = option_row.negative_prefix + option_row.prefix + Math.abs(result_value).toFixed(option_row.precision);
                                } else {
                                    result = option_row.positive_prefix + option_row.prefix + result;
                                }
                            }
                            if (option_row.postfix) {
                                result += option_row.postfix;
                            }
                        }
                        break;
                    case 'display_name':
                        let display_name = '';
                        let name_words = result.split('_');
                        name_words.forEach(function (name_word) {
                            if (!name_word.length) return;
                            name_word = name_word.trim();
                            if (name_word === 'id') return;
                            display_name += name_word.charAt(0).toUpperCase() + name_word.slice(1) + ' ';
                        });
                        display_name = display_name.trim();
                        result = display_name;
                        break;
                    case 'integer':
                        if (typeof result === 'string') {
                            result = result.replace(/[^\d.-]/ig, '');
                        }
                        result = parseFloat(result);
                        if (isNaN(result)) {
                            result = 0;
                        }
                        result = Math.round(result);
                        break;
                    case 'absolute_number':
                        if (typeof result === 'string') {
                            result = result.replace(/[^\d.]/ig, '');
                        } else {
                            result = Math.abs(result);
                        }
                        break;
                    case 'number_range':
                        result = parseFloat(result);
                        if (typeof result !== 'number') {
                            result = 0;
                        }
                        if (typeof option_row.min !== 'undefined' && result < option_row.min) {
                            result = option_row.min;
                        }
                        if (typeof option_row.max !== 'undefined' && result > option_row.max) {
                            result = option_row.max;
                        }
                        break;
                    case 'trim':
                        result = result.trim();
                        break;
                    case 'range':
                        let input_stack = input_value;
                        if (typeof input_stack === 'string') {
                            input_stack = input_value.split(',');
                        }

                        if (!input_stack.length || input_stack.length > 2) {
                            result = input_value;
                            break;
                        }
                        // let current_option_row_index = option.indexOf(option_row);
                        // if (current_option_row_index === -1) {
                        //     result = input_value;
                        //     break;
                        // }

                        result = '';
                        if (input_stack[0] !== '') {
                            if (option_row.start_date_prefix) {
                                result += option_row.start_date_prefix;
                            } else {
                                result += 'from';
                            }

                            result += ' ' + current_object.fix_format(input_stack[0], option.slice(option_row_index+1));
                            result = result.trim();
                        }
                        if (typeof input_stack[1] !== 'undefined' && input_stack[1] !== '') {
                            result += ' ';
                            if (option_row.end_date_prefix) {
                                result += option_row.end_date_prefix;
                            } else {
                                result += 'to';
                            }
                            result += ' ' + current_object.fix_format(input_stack[1], option.slice(option_row_index+1));
                            result = result.trim();
                        }
                        if (result) {
                            if (option_row.prefix) {
                                result = option_row.prefix + ' ' + result;
                                result = result.trim();
                            }
                            if (option_row.postfix) {
                                result += ' ' + option_row.postfix;
                            }
                            result = result.charAt(0).toUpperCase() + result.slice(1);
                        }

                        flag_end_loop = true;
                        break;
                    case 'register_company_party':
                        switch (input_value.contact_type) {
                            case 'Inidividual':
                                result = input_value.last_name;
                                if (input_value.middle_name) {
                                    result = input_value.middle_name + ' ' + result;
                                }
                                if (input_value.first_name) {
                                    result = input_value.first_name + ' ' + result;
                                }
                                if (input_value.title) {
                                    result = input_value.title + ' ' + result;
                                }
                                break;
                            case 'Joint Member':
                                if (!input_value._ref_list_children) {
                                    result = 'N/A';
                                    break;
                                }
                                let representative_name_list = [];
                                let beneficial_owner_name = '';
                                input_value._ref_list_children.forEach(function (sub_register_company_party) {
                                    if (sub_register_company_party.class === 'representative') {
                                        representative_name_list.push(current_object.fix_format(sub_register_company_party, ['register_company_party']));
                                    }
                                    if (sub_register_company_party.class === 'beneficial_owner') {
                                        beneficial_owner_name = current_object.fix_format(sub_register_company_party, ['register_company_party']);
                                    }
                                });
                                if (representative_name_list.length) {
                                    result = representative_name_list.pop();
                                    if (representative_name_list.length) {
                                        result = representative_name_list.join(', ') + ' and ' + result;
                                    }
                                    if (beneficial_owner_name) {
                                        result += ' as trustee for ' + beneficial_owner_name;
                                    }
                                } else {
                                    result = 'N/A';
                                }
                                result = 'Joint Member: ' + result;
                                break;
                            case 'Company':
                            default:
                                result = input_value.name;
                                if (input_value.business_number) {
                                    result += ' (A.B.N. ' + input_value.business_number + ')';
                                } else {
                                    if (input_value.company_number) {
                                        result += ' (A.C.N. ' + input_value.company_number + ')';
                                    }
                                }
                                break;
                        }
                        break;
                    case 'trust_name':
                        result = result.trim();
                        if (result === '') {
                            result = '';
                            break;
                        }
                        if (!result.match(/^The /)) {
                            if (result.toLowerCase().match(/^the\s/)) {
                                result = result.substring(4).trim();
                            }
                            result = 'The ' + result;
                        }
                        if (!result.match(/ Trust$/)) {
                            if (result.toLowerCase().match(/\strust$/)) {
                                result = result.substring(0, result.length - 6).trim();
                            }
                            result += ' Trust';
                        }
                        break;
                    case 'item_list_formatted_name':
                        if (!Array.isArray(input_value)) {
                            break;
                        }
                        if (!(option_row['exclude_name_list'])) {
                            option_row['exclude_name_list'] = [];
                        }
                        if (!(option_row['relationship'])) {
                            option_row['relationship'] = 'and';
                        }
                        let name_list = [];
                        input_value.forEach(function (item) {
                            if ((item['formatted_name']) && option_row['exclude_name_list'].indexOf(item['formatted_name']) === -1) {
                                name_list.push(item['formatted_name']);
                            }
                        });
                        result = '';
                        if (name_list.length) {
                            result = name_list.pop();
                            if (name_list.length) {
                                result = name_list.join(', ') + ' ' + option_row['relationship'] + ' ' + result;
                            }
                        }
                        break;

                }
                if (flag_end_loop) {
                    break;
                }
            }
            // };
            return result;
        },
        // General list related functions
        // list suggest root.item_list or any array in similar structure, e.g. [module_item1, module_item2...]
        // item suggest module item or any object in similar structure, e.g {id:x, name:xxx, display_name:xxx, ...}
        copy_list: function (source_list, list, flag_keep_target_list) {
            if (!Array.isArray(source_list)) {
                console.log('Warning: copy_list -> source_list is not an Array',source_list,typeof source_list);
                return list;
            }
            if (!Array.isArray(list)) {
                list = [];
            }
            // Keep original list (array object), but replace all items inside with items from given source_list
            if (!flag_keep_target_list) {
                list.splice(0,list.length);
            }
            source_list.forEach(function (item) {
                list.push(item);
            });
            return list;
        },
        copy_item: function(source_item, item) {
            let current_object = this;
            // When use object in item_map, cross reference make it impossible for axio post, remove the object children to make it postable
            // This function is generally used for forms, convert item_map object into cache_data object
            if (typeof item === 'undefined') {
                item = {};

                item = Object.assign(item, source_item);
                Object.keys(item).forEach(function (key) {
                    if (item[key] && typeof item[key] === 'object') {
                        try {
                            item[key] = JSON.parse(JSON.stringify(source_item[key]));
                        } catch (e) {
                            delete item[key];
                        }
                    }
                });
            } else {
                Object.keys(item).forEach(function (key) {
                    switch (typeof source_item[key]) {
                        case 'undefined':
                            // Source does not have this property, leave item property unchanged
                            break;
                        case 'object':
                            if (!source_item[key]) {
                                // directly set value for null object
                                item[key] = source_item[key];
                                break;
                            }
                            try {
                                item[key] = JSON.parse(JSON.stringify(source_item[key]));
                            } catch (e) {
                                console.log('Error on import field, unable to clone object',key,source_item[key]);
                                delete item[key];
                            }
                            break;
                        default:
                            if (key === 'password') {
                                // password reset to empty instead of original data
                                item[key] = '';
                                break;
                            }
                            item[key] = source_item[key];
                    }
                });
            }

            return item;
        },
        add_to_list: function (item, list, order_by, order_seq) {
            let index = list.indexOf(item);
            if (index > -1) {
                // item already inside list, skip adding
                return list;
            }
            list.push(item);
            if (order_seq !== 'desc') {
                order_seq = 'asc';
            }
            if (typeof order_by === 'undefined') {
                return list;
            }
            if (order_by === '_value') {
                let temp_list = list;
                list.sort();
                if (order_seq === 'desc') {
                    list.reverse();
                }
                return list;
            }

            let ordered_list = _.orderBy(list, order_by, order_seq);
            this.copy_list(list, ordered_list);
        },
        remove_from_list: function (item, list) {
            // Remove specific item from list (item object must be consistent)
            let index = list.indexOf(item);
            if (index > -1) {
                list.splice(index,1);
            }
        },

        import_item: function(source, target) {
            let current_object = this;
            // When use object in item_map, cross reference make it impossible for axio post, remove the object children to make it postable
            // This function is generally used for forms, convert item_map object into cache_data object
            if (typeof target === 'undefined') {
                target = {};

                target = Object.assign(target, source);
                Object.keys(source).forEach(function (key) {
                    if (/^_/.test(key)) {
                        // fields leading by _ are fields for special functions
                        delete target[key];
                        return false;
                    }
                    if (source[key] && typeof source[key] === 'object') {
                        // Note: null value is an object,
                        let flag_remove = true;
                        // keep array with none object children, for example, in source:{a:{},b:[],c:[{},{},{}],d:[1,2,3]}, b and d would be imported, a and c would be deleted

                        if (typeof source[key].length !== 'undefined') {
                            if (typeof source[key][0] !== 'object') {
                                flag_remove = false;
                            }
                        }
                        let prototype = Object.getPrototypeOf(source[key]);
                        if (prototype && prototype.constructor.name === 'File') {
                            flag_remove = false;
                        }
                        if (flag_remove) {
                            delete target[key];
                        }
                    }
                });
            } else {
                Object.keys(target).forEach(function (key) {
                    switch (typeof source[key]) {
                        case 'undefined':
                            // Source does not have this property, leave target property unchanged
                            break;
                        case 'object':
                            if (!source[key]) {
                                // directly set value for null object
                                target[key] = source[key];
                                break;
                            }
                            if (typeof source[key].length !== 'undefined') {
                                // If source[key] is an array, import each item
                                if (Array.isArray(target[key])) {
                                    target[key].splice(0,target[key].length);
                                } else {
                                    target[key] = [];
                                }
                                source[key].forEach(function (source_item) {
                                    if (typeof source_item === 'object') {
                                        target[key].push(current_object.import_item(source_item));
                                    } else {
                                        target[key].push(source_item);
                                    }
                                })
                            } else {
                                target[key] = current_object.import_item(source[key]);
                            }
                            break;
                        default:
                            if (key === 'password') {
                                // password reset to empty instead of original data
                                target[key] = '';
                                break;
                            }
                            target[key] = source[key];
                    }
                    //
                    // if (typeof source[key] !== 'undefined') {
                    //     target[key] = source[key];
                    // }
                });
            }

            Object.keys(target).forEach(function (key) {
                if (key.match(/_proportion$/)) {
                    target[key] = String(parseInt(target[key]));
                }
            });

            let source_party_list = source._ref_list_trust_party || source._ref_list_super_party || source._ref_list_register_company_party || source._ref_list_loan_agreement_party || source._ref_list_gift_deed_party;
            if (source_party_list) {
                if (!target.sorted_parties) {
                    target.sorted_parties = {};
                } else {
                    Object.keys(target.sorted_parties).forEach(function (party_class) {
                        target.sorted_parties[party_class].splice(0,target.sorted_parties[party_class].length);
                        if (target[party_class]) {
                            target[party_class].splice(0,target[party_class].length);
                        }
                    });
                }
                let source_party_map = {};
                source_party_list.forEach(function (source_party) {
                    if (!target[source_party.class]) {
                        target[source_party.class] = [];
                    } else {
                        target[source_party.class].splice(0,target[source_party.class].length);
                    }
                    if (!target.sorted_parties[source_party.class]) {
                        target.sorted_parties[source_party.class] = [];
                    } else {
                        target.sorted_parties[source_party.class].splice(0,target.sorted_parties[source_party.class].length);
                    }
                    source_party_map[source_party.id] = current_object.import_item(source_party);
                });
                let beneficiary_grandparent_is_principal_beneficiary = [];
                let beneficiary_grandparent_is_appointor = [];
                let beneficiary_parent_is_principal_beneficiary = [];
                let beneficiary_parent_is_appointor = [];
                let beneficiary_child_is_child_receiving_loan = [];
                Object.keys(source_party_map).forEach(function (source_party_id) {
                    let source_party_class = source_party_map[source_party_id].class;
                    target[source_party_class].push(source_party_id);
                    target.sorted_parties[source_party_class].push(source_party_map[source_party_id]);
                    if (source_party_class === 'beneficiary_grandparent') {
                        if (source_party_map[source_party_id].is_appointor) {
                            beneficiary_grandparent_is_appointor.push(source_party_id);
                        }
                        if (source_party_map[source_party_id].is_principal_beneficiary) {
                            beneficiary_grandparent_is_principal_beneficiary.push(source_party_id);
                        }
                    }
                    if (source_party_class === 'beneficiary_parent') {
                        if (source_party_map[source_party_id].is_appointor) {
                            beneficiary_parent_is_appointor.push(source_party_id);
                        }
                        if (source_party_map[source_party_id].is_principal_beneficiary) {
                            beneficiary_parent_is_principal_beneficiary.push(source_party_id);
                        }
                    }
                    if (source_party_class === 'beneficiary_child') {
                        if (source_party_map[source_party_id].is_child_receiving_loan) {
                            beneficiary_child_is_child_receiving_loan.push(source_party_id);
                        }
                    }
                });
                target.beneficiary_grandparent_is_principal_beneficiary = '';
                if (beneficiary_grandparent_is_principal_beneficiary.length === 1) {
                    target.beneficiary_grandparent_is_principal_beneficiary = beneficiary_grandparent_is_principal_beneficiary[0];
                }
                target.beneficiary_grandparent_is_appointor = '';
                if (beneficiary_grandparent_is_appointor.length) {
                    if (beneficiary_grandparent_is_appointor.length === 1) {
                        target.beneficiary_grandparent_is_appointor = beneficiary_grandparent_is_appointor[0];
                    }
                    if (beneficiary_grandparent_is_appointor.length === target['beneficiary_grandparent'].length) {
                        target.beneficiary_grandparent_is_appointor = 'all';
                    }
                }
                target.beneficiary_parent_is_principal_beneficiary = '';
                if (beneficiary_parent_is_principal_beneficiary.length === 1) {
                    target.beneficiary_parent_is_principal_beneficiary = beneficiary_parent_is_principal_beneficiary[0];
                }
                target.beneficiary_parent_is_appointor = '';
                if (beneficiary_parent_is_appointor.length) {
                    if (beneficiary_parent_is_appointor.length === 1) {
                        target.beneficiary_parent_is_appointor = beneficiary_parent_is_appointor[0];
                    }
                    if (beneficiary_parent_is_appointor.length === target['beneficiary_parent'].length) {
                        target.beneficiary_parent_is_appointor = 'all';
                    }
                }
                target.beneficiary_child_is_child_receiving_loan = '';
                if (beneficiary_child_is_child_receiving_loan.length === 1) {
                    target.beneficiary_child_is_child_receiving_loan = beneficiary_child_is_child_receiving_loan[0];
                }
            } else {
                if (target.sorted_parties && source.sorted_parties) {
                    Object.keys(source.sorted_parties).forEach(function (party_class) {
                        // target['address_map'] = {};
                        target[party_class] = [];
                        target.sorted_parties[party_class] = [];
                        source.sorted_parties[party_class].forEach(function (source_party) {
                            target[party_class].push(source_party.id);
                            target.sorted_parties[party_class].push(current_object.import_item(source_party));
                            // if (source_party.address) {
                            //     target['address_map'][source_party.address_id] = source_party.address;
                            // }
                        });
                    });
                }
            }

            return target;
        },

    },
};

// ── Create Vue 3 app and register components ────────────────────────────────

const app = createApp(appOptions);

// Register dateFormat as a global property (replaces Vue.filter in Vue 2)
app.config.globalProperties.$dateFormat = dateFormat;

// Guided walkthroughs (Shepherd.js). Remove this line + delete resources/js/tours/ to uninstall.
installTours(app);

// Misc / Shared components — all sync; tiny + used everywhere.
app.component('login-form', LoginForm);
app.component('top-menu', TopMenu);
app.component('news-headlines', NewsHeadlines);
app.component('item-list', ItemList);
app.component('not-found', MiscNotFound);
app.component('multi-select-checkbox', MultiCheckbox);
app.component('display-message', DisplayMessage);

// Form components — all lazy. Each page-level form is its own chunk.
// FormOrderCompany / FormOrderTrust / FormOrderSuper are the biggest
// single forms in the app; UpdateItem/UpdateUser/etc. are heavy too.
// None are needed until the user navigates to a /forms/* page.
app.component('form-contact-us', lazy(() => import('./components/form/ContactUs.vue')));
app.component('form-order-company', lazy(() => import('./components/form/OrderCompany.vue')));
app.component('form-order-super', lazy(() => import('./components/form/OrderSuper.vue')));
app.component('form-order-trust', lazy(() => import('./components/form/OrderTrust.vue')));
app.component('form-update-company', lazy(() => import('./components/form/UpdateCompany.vue')));
app.component('form-update-credit-card', lazy(() => import('./components/form/UpdateCreditCard.vue')));
app.component('form-update-document', lazy(() => import('./components/form/UpdateDocument.vue')));
app.component('form-update-news-headlines', lazy(() => import('./components/form/UpdateNewsHeadlines.vue')));
app.component('form-update-user', lazy(() => import('./components/form/UpdateUser.vue')));
app.component('form-update-tv-show', lazy(() => import('./components/form/UpdateTvShow.vue')));
app.component('form-update-fte-review', lazy(() => import('./components/form/UpdateFteReview.vue')));
app.component('form-update-item', lazy(() => import('./components/form/UpdateItem.vue')));

// Subform components — sync. These are nested inside the form chunks
// above and Rollup will inline them into whichever parent's chunk
// references them.
app.component('add-new-address', AddNewAddress);
app.component('update-contact', UpdateContact);
app.component('update-officer', UpdateOfficer);
app.component('update-shareholder', UpdateShareholder);
app.component('update-register-company-party', UpdateRegisterCompanyParty);
app.component('update-address', UpdateAddress);
app.component('update-party', UpdateParty);
app.component('update-trust-party', UpdateTrustParty);
app.component('update-document-brand', UpdateDocumentBrand);
app.component('update-image', UpdateImage);
app.component('update-sub-item', UpdateSubItem);
app.component('update-fte-review-entity', UpdateFteReviewEntity);

// Admin panel components — all lazy. Only admin users hit these so
// every non-admin saves the entire admin subtree (~hundreds of kB)
// from their main bundle.
app.component('admin-view-company', lazy(() => import('./components/admin/ViewCompany.vue')));
app.component('manage-training-modules', lazy(() => import('./components/admin/ManageTrainingModules.vue')));
app.component('admin-disbursements', lazy(() => import('./components/admin/Disbursements.vue')));
app.component('manage-macquarie-tv', lazy(() => import('./components/admin/ManageMacquarieTv.vue')));
app.component('edit-training-module', lazy(() => import('./components/admin/EditTrainingModule.vue')));

// Client panel components.
//   Sync: layout shells + ClientHome (default landing — keeping sync
//   avoids a network round-trip on the very first page render).
//   Lazy: everything else. ClientAml is the headline split — the
//   entire AML SPA subtree (CDD, escalations, reporting, associated
//   persons, ~1.5MB of code) is now only downloaded when a user
//   actually opens /client/aml/* instead of bloating every cold load.
app.component('client-default', ClientDefault);
app.component('client-content', ClientContent);
app.component('client-content-with-left-menu', ClientContentWithLeftMenu);
app.component('client-home', ClientHome);
app.component('client-not-found', ClientNotFound);
app.component('client-contact-us', lazy(() => import('./components/client/ContactUs.vue')));
app.component('client-products', lazy(() => import('./components/client/Products.vue')));
app.component('client-companies', lazy(() => import('./components/client/Companies.vue')));
app.component('client-trusts', lazy(() => import('./components/client/Trusts.vue')));
app.component('client-hybrid-trust-solutions', lazy(() => import('./components/client/HybridTrustSolutions.vue')));
app.component('client-warrant-deeds', lazy(() => import('./components/client/WarrantDeeds.vue')));
app.component('client-superannuation', lazy(() => import('./components/client/Superannuation.vue')));
app.component('client-customised-products', lazy(() => import('./components/client/CustomisedProducts.vue')));
app.component('client-branded-documents', lazy(() => import('./components/client/BrandedDocuments.vue')));
app.component('client-services', lazy(() => import('./components/client/Services.vue')));
// Two aliases (typo'd + correct) share one chunk via single
// dynamic-import expression; Vite emits one chunk and reuses it.
const lazyMacquarieTV = lazy(() => import('./components/client/MaquareTV.vue'));
app.component('client-macquare-tv', lazyMacquarieTV);
app.component('client-macquarie-tv', lazyMacquarieTV);
app.component('client-documents', lazy(() => import('./components/client/Documents.vue')));
app.component('client-about', lazy(() => import('./components/client/About.vue')));
app.component('client-succession-trusts', lazy(() => import('./components/client/SuccessionTrusts.vue')));
app.component('client-bomad-trusts', lazy(() => import('./components/client/BomadTrusts.vue')));
app.component('client-bonap-trusts', lazy(() => import('./components/client/BonapTrusts.vue')));
app.component('client-bomad-guide', lazy(() => import('./components/client/BomadGuide.vue')));
app.component('client-webinar', lazy(() => import('./components/client/Webinar.vue')));
app.component('client-my-superannuations', lazy(() => import('./components/client/MySuperannuations.vue')));
app.component('client-my-trusts', lazy(() => import('./components/client/MyTrusts.vue')));
app.component('client-my-company-registrations', lazy(() => import('./components/client/MyCompanyRegistrations.vue')));
app.component('client-my-loan-agreements', lazy(() => import('./components/client/MyLoanAgreements.vue')));
app.component('client-my-gift-deeds', lazy(() => import('./components/client/MyGiftDeeds.vue')));
app.component('client-my-details', lazy(() => import('./components/client/MyDetails.vue')));
app.component('client-aml', lazy(() => import('./components/client/Aml.vue')));

// Phase 5 Tier 1 — per-product Vue twins for the redesigned frontend
// pages that previously fell back to Default.vue when accessed inside
// the client SPA. Each twin renders the same content as its Blade
// counterpart in resources/views/front/products/* (or front/pricing
// / front/mgs_private/about_mgs_private for the two outliers).
app.component('client-pricing',                       lazy(() => import('./components/client/Pricing.vue')));
app.component('client-about-mgs-private',             lazy(() => import('./components/client/AboutMgsPrivate.vue')));
app.component('client-discretionary-trust',           lazy(() => import('./components/client/DiscretionaryTrust.vue')));
app.component('client-discretionary-conversion-trust', lazy(() => import('./components/client/DiscretionaryConversionTrust.vue')));
app.component('client-unit-trust',                    lazy(() => import('./components/client/UnitTrust.vue')));
app.component('client-capital-pool-unit-trust',       lazy(() => import('./components/client/CapitalPoolUnitTrust.vue')));
app.component('client-nsw-land-tax-unit-trust',       lazy(() => import('./components/client/NswLandTaxUnitTrust.vue')));
app.component('client-hybrid-discretionary-trust',    lazy(() => import('./components/client/HybridDiscretionaryTrust.vue')));
app.component('client-hybrid-unit-trust',             lazy(() => import('./components/client/HybridUnitTrust.vue')));
app.component('client-vic-property-trust',            lazy(() => import('./components/client/VicPropertyTrust.vue')));
app.component('client-wa-property-trust',             lazy(() => import('./components/client/WaPropertyTrust.vue')));
app.component('client-smsf-kits-features',            lazy(() => import('./components/client/SmsfKitsFeatures.vue')));

// Phase 5 Tier 2 — MGS Private leaves (32 pages). Each is a thin
// data-only wrapper around the shared MgsPrivateLeaf dispatcher
// (resources/js/components/client/mgs_private/MgsPrivateLeaf.vue),
// which in turn imports the 7 body-type components. The .rd-mp
// design system is loaded globally via _redesign-mgs-private.scss.
app.component('client-advice-on-present-entitlements-and-specific-entitlements',           lazy(() => import('./components/client/mgs_private/AdviceOnPresentEntitlementsAndSpecificEntitlements.vue')));
app.component('client-amending-deeds-for-succession-planning',                              lazy(() => import('./components/client/mgs_private/AmendingDeedsForSuccessionPlanning.vue')));
app.component('client-amending-trust-deeds-to-account-act-2011',                            lazy(() => import('./components/client/mgs_private/AmendingTrustDeedsToAccountAct2011.vue')));
app.component('client-ato-tax-disputes-and-audits',                                         lazy(() => import('./components/client/mgs_private/AtoTaxDisputesAndAudits.vue')));
app.component('client-consolidations-and-restructures',                                     lazy(() => import('./components/client/mgs_private/ConsolidationsAndRestructures.vue')));
app.component('client-corporate-consolidation-restructure-services',                        lazy(() => import('./components/client/mgs_private/CorporateConsolidationRestructureServices.vue')));
app.component('client-design-and-building-practitioners-act-2020',                          lazy(() => import('./components/client/mgs_private/DesignAndBuildingPractitionersAct2020.vue')));
app.component('client-extending-the-vesting-date-of-trusts',                                lazy(() => import('./components/client/mgs_private/ExtendingTheVestingDateOfTrusts.vue')));
app.component('client-high-wealth-individuals',                                             lazy(() => import('./components/client/mgs_private/HighWealthIndividuals.vue')));
app.component('client-insertion-of-holding-companies-into-groups',                          lazy(() => import('./components/client/mgs_private/InsertionOfHoldingCompaniesIntoGroups.vue')));
app.component('client-land-tax-audits-objections-and-appeals',                              lazy(() => import('./components/client/mgs_private/LandTaxAuditsObjectionsAndAppeals.vue')));
app.component('client-landholder-duty-advice',                                              lazy(() => import('./components/client/mgs_private/LandholderDutyAdvice.vue')));
app.component('client-long-term-construction-contracts',                                    lazy(() => import('./components/client/mgs_private/LongTermConstructionContracts.vue')));
app.component('client-moving-property-in-queensland-between-trusts-without-stamp-duty',     lazy(() => import('./components/client/mgs_private/MovingPropertyInQueenslandBetweenTrustsWithoutStampDuty.vue')));
app.component('client-moving-property-in-western-australia-without-stamp-duty',             lazy(() => import('./components/client/mgs_private/MovingPropertyInWesternAustraliaWithoutStampDuty.vue')));
app.component('client-nsw-land-tax',                                                        lazy(() => import('./components/client/mgs_private/NswLandTax.vue')));
app.component('client-nsw-land-tax-strategy-corporate-consolidation-of-unit-trust',         lazy(() => import('./components/client/mgs_private/NswLandTaxStrategyCorporateConsolidationOfUnitTrust.vue')));
app.component('client-nsw-land-tax-strategy-trust-conversion-family-home',                  lazy(() => import('./components/client/mgs_private/NswLandTaxStrategyTrustConversionFamilyHome.vue')));
app.component('client-nsw-land-tax-strategy-trust-conversion-investment-property',          lazy(() => import('./components/client/mgs_private/NswLandTaxStrategyTrustConversionInvestmentProperty.vue')));
app.component('client-objections-and-appeals-to-assessments-and-decisions',                 lazy(() => import('./components/client/mgs_private/ObjectionsAndAppealsToAssessmentsAndDecisions.vue')));
app.component('client-payroll-tax-audits-objections-and-appeals',                           lazy(() => import('./components/client/mgs_private/PayrollTaxAuditsObjectionsAndAppeals.vue')));
app.component('client-property-development-structures-asset-protection',                    lazy(() => import('./components/client/mgs_private/PropertyDevelopmentStructuresAssetProtection.vue')));
app.component('client-rollovers-restructures-and-consolidations',                           lazy(() => import('./components/client/mgs_private/RolloversRestructuresAndConsolidations.vue')));
app.component('client-small-business-cgt-concessions',                                      lazy(() => import('./components/client/mgs_private/SmallBusinessCgtConcessions.vue')));
app.component('client-small-business-restructures',                                         lazy(() => import('./components/client/mgs_private/SmallBusinessRestructures.vue')));
app.component('client-stamp-duty-audits-objections-and-appeals',                            lazy(() => import('./components/client/mgs_private/StampDutyAuditsObjectionsAndAppeals.vue')));
app.component('client-structuring-to-avoid-unintended-capital-gains',                       lazy(() => import('./components/client/mgs_private/StructuringToAvoidUnintendedCapitalGains.vue')));
app.component('client-the-problem-with-partitions',                                         lazy(() => import('./components/client/mgs_private/TheProblemWithPartitions.vue')));
app.component('client-transfers-amongst-subsidiary-entities',                               lazy(() => import('./components/client/mgs_private/TransfersAmongstSubsidiaryEntities.vue')));
app.component('client-trust-cloning-for-cgt',                                               lazy(() => import('./components/client/mgs_private/TrustCloningForCgt.vue')));
app.component('client-wa-small-business-cgt-rollovers-without-duty',                        lazy(() => import('./components/client/mgs_private/WaSmallBusinessCgtRolloversWithoutDuty.vue')));
app.component('client-wa-small-business-restructures-without-duty',                         lazy(() => import('./components/client/mgs_private/WaSmallBusinessRestructuresWithoutDuty.vue')));


// Global error handler – surfaces Vue runtime errors in the console
app.config.errorHandler = (err, vm, info) => {
    console.error('[Vue Error]', info, err);
};

// Read init data from the DOM BEFORE mount() clears the container innerHTML
const _appEl = document.querySelector('#app');
const _appDataEl = _appEl ? _appEl.querySelector('.app_data') : null;
window.__initData = (_appDataEl && _appDataEl.dataset.init) ? _appDataEl.dataset.init : null;
window.__appRootClasses = _appEl ? Array.from(_appEl.classList) : [];

// Mount the app
app.mount('#app');

// Swap out the boot skeleton (if present) and reveal the mounted app.
// The client panel and AML panel render a skeleton before Vue boots so that
// cross-app navigation (e.g. AML → /client/order-trust) feels responsive.
(function () {
    var appEl = document.getElementById('app');
    var skelEl = document.getElementById('client_boot_skel_wrap');
    if (appEl && appEl.style.display === 'none') appEl.style.display = '';
    if (skelEl) skelEl.parentNode.removeChild(skelEl);
})();
